// Office call-out page. Two intakes, and the Log intake takes two kinds of customer:
//  - Log Call-out / Existing customer: an existing customer's lift (customer -> site
//    -> lift cascade); raises a DRAFT invoice at the in-contract tier rate.
//  - Log Call-out / New customer (Session AEP): a walk-in or non-contract caller the
//    office wants a LASTING record for. Creates a real Customer, Site and lift, then
//    logs the call-out against that lift down the ordinary path above (same dispatch,
//    same DRAFT invoicing) at the non-contract rate, since the new customer has no
//    contract. The customer, site and lift then behave like any other record on the
//    registers, so the visit builds a real service history and the customer can be
//    put on contract later without anyone touching the database.
//  - Emergency Call-out: a non-contract caller (free-text details); raises a DRAFT
//    invoice at the non-contract rate (£326 ex VAT), attaches the Schedule of Fees
//    and sends the caller a branded confirmation email. Deliberately keeps NO
//    Customer/Site/Asset record: it remains the right tool for a genuinely one-off
//    caller nobody wants a lasting record for.
// Below the forms is the list of call-outs raised so far. The dashboard quick
// action links here with ?new=log to open the Log call-out form, which carries an
// entrapment toggle (Session ACY: the separate Emergency dashboard button was retired
// and entrapment folded into this flow). ?new=emergency opens the non-contract
// emergency form; ?new=entrapment opens the Log form with the entrapment toggle on.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { FormEvent } from "react";
import { AlertTriangle, Phone } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { ApiError, api } from "../../lib/api";
import type { CalloutListItem, EmergencyCalloutResult, ScrubSummary, AssignableEngineer, CallerMatchResult } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { ConfirmDeleteModal, DELETE_CONFIRM_MESSAGE } from "../../components/ConfirmDeleteModal";
import { ConfirmButton } from "../../components/ConfirmButton";
import { AccountOnStopLine, AccountSymbol } from "../../components/AccountSymbol";
import { OverflowMenu, overflowItemCls, overflowDangerItemCls } from "../../components/OverflowMenu";
import { calloutRowActions } from "./calloutRowActions";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { CalloutChargeModal } from "./CalloutChargeModal";
import { CalloutPhotosModal } from "./CalloutPhotosModal";
import { CalloutReportModal, type CalloutReportInitial } from "./CalloutReportModal";
import { CalloutReportRefModal } from "./CalloutReportRefModal";
import { CancelJobModal } from "./CancelJobModal";
import { JobSheetList } from "./JobSheetList";
import { RecordPicker } from "../../components/RecordPicker";
import { buildReportInitial, storedFormSiteId } from "./calloutReportSeed";
import { useNewCustomerChain, CHAIN_STEP_LABEL } from "../../lib/useNewCustomerChain";
import { estimateCallout, TRAVEL_MINS_OPTIONS, parseAgreedPrice } from "./calloutRates";
import type { CalloutTier, AgreedPriceParse } from "./calloutRates";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../components/ModalShell";
import { ProposedSlotLine } from "../../components/ProposedSlotLine";
import { initialMobileLayout } from "../../lib/mobileLayout";
import { MobileCardList, MobileListCard } from "../../components/MobileListCard";
import { SortableHeader } from "../../components/SortableHeader";
import { useDebouncedValue } from "../../lib/useDebouncedValue";
import { Pager } from "../../components/Pager";
import { ExportCsvMenu } from "../../components/ExportCsvMenu";
import { nextSortState } from "./register/registerTable";
import { CALLOUT_PAGE_SIZE, calloutPageQuery, hasActiveFilters, parseCalloutFilters, parseSortState, type CalloutPageQuery } from "./calloutListFilters";
import { CalloutsFilterBar } from "./CalloutsFilterBar";
import { Button, buttonClasses } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import { Loader } from "../../components/Loader";
import { PageHeader } from "../../components/PageHeader";
import { StatusPill, toneTextClass } from "../../components/StatusPill";
import { holdLabel } from "../../lib/jobHold";
import { primaryStatePill, STATUS_REGISTRY, type StatePillSpec } from "../../lib/statusRegistry";
import { statusEntry } from "../../lib/statusRegistry";
import { formatDate } from "../../lib/format";
import { ErasedPersonNotice } from "../../components/ErasedPersonNotice";

const LOG_TIERS: { tier: CalloutTier; label: string }[] = [
  { tier: "core", label: "Core" },
  { tier: "care", label: "Care" },
  { tier: "complete", label: "Complete" },
];

// The lift types offered when the office is creating a lift record on the spot (Session AEP).
// Asset.liftType is free text on the register, so these are plain words, matching what the office
// already types there. "Not yet known" writes nothing rather than guessing: the engineer will see
// the real lift and the office can set it afterwards.
// Exported: RaiseWorkModal.tsx's own "lift not on the register yet" chain (Session AOI) offers the
// same words for the same reason, and a second, driftable copy of this list is worse than a
// cross-page import this codebase already uses elsewhere (e.g. sentenceCaseStatus below).
export const NEW_LIFT_TYPES = [
  { value: "", label: "Not yet known" },
  { value: "Passenger", label: "Passenger" },
  { value: "Goods", label: "Goods" },
  { value: "Platform", label: "Platform" },
  { value: "Stairlift", label: "Stairlift" },
];

// ChainStep / CHAIN_STEP_LABEL now live in src/lib/useNewCustomerChain.ts (Session AOI), imported
// above, alongside the resumable Customer -> Site -> Asset chain itself.

// The "call-out logged" confirmation. The server's `ref` is the lift's ELE number, else the job
// reference, else the literal word "Call-out" when the lift has neither. That fallback is not a
// reference and must not be printed as one: a new customer's lift has no ELE number by design, so
// the message otherwise read "Call-out Call-out logged for ...". Pure, so it is unit tested.
export function calloutLoggedMessage(ref: string, siteName: string, isNewCustomer: boolean, invoiced?: boolean, agreedFreeOfCharge?: boolean): string {
  const named = ref && ref.trim() && ref.trim().toLowerCase() !== "call-out" ? `Call-out ${ref.trim()}` : "The call-out";
  const tail = isNewCustomer
    ? "The customer, site and lift are now on the registers, so this visit builds their service history."
    : "It now appears in the list below.";
  // The server says whether a DRAFT was actually raised. It may not have been: billing automation
  // can be off, Xero can be disconnected, or a Complete-tier call-out is bundled. Saying only
  // "logged" beside an on-screen estimate implies an invoice exists, so when none does, say so.
  // Session AEW review fix: an agreed price of ZERO means there is deliberately nothing to bill,
  // so telling the office to bill it by hand is the opposite of what was agreed. Say what
  // actually happened instead.
  const invoice = agreedFreeOfCharge
    ? " It was agreed free of charge, so no invoice was raised."
    : invoiced === false
      ? " No draft invoice was raised, so bill this one by hand."
      : "";
  return `${named} was logged for ${siteName}. ${tail}${invoice}`;
}

// The colour of a call-out's status WORD comes from the status registry (the `job` domain, the one
// canonical map), never a local table. The word itself stays a plain sentence-case value, not a pill
// (Session AGZ). Design system Session 4: a pending_approval call-out now reads amber (it fell through
// to the body colour here).
function statusWordClass(status: string): string {
  return toneTextClass(statusEntry("job", status).tone);
}

// Session AGZ sentence case: the raw status enum ("in_progress") rendered as a readable
// sentence-case word ("In progress") -- the caps CSS override is retired. Exported for the
// SchedulingPage popover's Status row (the fmtTrainingDate cross-page precedent), so the
// two surfaces cannot drift on the rendering.
export function sentenceCaseStatus(status: string): string {
  // Session BEG (the release gate): a closed visit waiting for the office reads "Closed, awaiting release", from the one registry,
  // never the raw enum's "Pending approval". Every other status keeps its sentence-case word.
  if (status === "pending_approval") return statusEntry("job", status).label;
  const words = status.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

// Empty-cell placeholder. A single plain dash, never "--" (which reads as a glitch
// in the UI and clashes with the em-dash-substitute house rule used in prose).
const EMPTY = "-";

// datetime-local default ("YYYY-MM-DDTHH:mm") in local time, for the emergency form.
function nowLocalInput(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function gbp(n: number): string {
  return `£${n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Out-of-hours toggle + travel-time dropdown, shared by both call-out forms. The
// two cells slot into the parent grid.
function RateControls({
  idPrefix,
  ooh,
  onOoh,
  travel,
  onTravel,
}: {
  idPrefix: string;
  ooh: boolean;
  onOoh: (v: boolean) => void;
  travel: number;
  onTravel: (v: number) => void;
}) {
  return (
    <>
      <div>
        <span className="mb-1 block text-sm">Hours</span>
        <label htmlFor={`${idPrefix}-ooh`} className="flex items-center gap-2 rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2">
          <input id={`${idPrefix}-ooh`} type="checkbox" checked={ooh} onChange={(e) => onOoh(e.target.checked)} />
          <span className="text-sm">Out of hours (the call-out rule: weekends, bank holidays, before 09:00 or after 17:00).</span>
        </label>
      </div>
      <div>
        <label className="mb-1 block text-sm" htmlFor={`${idPrefix}-travel`}>Travel time</label>
        <select
          id={`${idPrefix}-travel`}
          value={travel}
          onChange={(e) => onTravel(Number(e.target.value))}
          className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green"
        >
          {TRAVEL_MINS_OPTIONS.map((m) => (
            <option key={m} value={m}>
              {m === 0 ? "No travel" : `${m} mins`}
            </option>
          ))}
        </select>
      </div>
    </>
  );
}

// The one-off agreed price for a single call-out (Session AEW), shared by both intake forms. Two
// grid cells, like RateControls, so it slots into the same parent grid.
//
// The price is typed in POUNDS because that is what was said on the phone; parseAgreedPrice turns
// it into the pence the API takes. Leaving it blank is the normal case and changes nothing.
function AgreedPriceFields({
  idPrefix,
  price,
  onPrice,
  reason,
  onReason,
  parsed,
}: {
  idPrefix: string;
  price: string;
  onPrice: (v: string) => void;
  reason: string;
  onReason: (v: string) => void;
  parsed: AgreedPriceParse;
}) {
  const errorId = `${idPrefix}-agreed-error`;
  // Session AFO: an agreed price requires a reason (matching the quote manual price override). It is
  // required the moment a valid figure is typed (parsed.state === "ok"), which includes an agreed
  // zero ("free of charge"). The submit is gated on the same condition in the parent, so this inline
  // message explains why the button is disabled.
  const reasonRequired = parsed.state === "ok";
  const reasonMissing = reasonRequired && reason.trim().length === 0;
  const reasonErrorId = `${idPrefix}-agreed-reason-error`;
  return (
    <>
      <div>
        <label className="mb-1 block text-sm" htmlFor={`${idPrefix}-agreed`}>Agreed price (ex VAT)</label>
        <input
          id={`${idPrefix}-agreed`}
          // Not type="number": a spinner invites a scroll wheel to change a money figure, and the
          // browser's own numeric validation would silently blank a value it disliked rather than
          // telling the office what is wrong. inputMode gets the numeric keypad on a phone.
          type="text"
          inputMode="decimal"
          autoComplete="off"
          value={price}
          onChange={(e) => onPrice(e.target.value)}
          placeholder="Leave blank for the standard rate"
          aria-invalid={parsed.state === "invalid"}
          aria-describedby={parsed.state === "invalid" ? errorId : undefined}
          className={`w-full rounded-ele border bg-ele-field px-3 py-2 outline-none focus:border-ele-green ${
            parsed.state === "invalid" ? "border-ele-danger" : "border-ele-purple-accent"
          }`}
        />
        {parsed.state === "invalid" ? (
          <p id={errorId} role="alert" className="mt-1 text-xs text-ele-danger">{parsed.message}</p>
        ) : (
          <p className="mt-1 text-xs text-ele-body opacity-70">
            A price agreed for this one call-out, for example a figure settled on the phone. Leave it blank to use the standard rate.
          </p>
        )}
      </div>
      <div>
        <label className="mb-1 block text-sm" htmlFor={`${idPrefix}-agreed-reason`}>
          Reason for the agreed price {reasonRequired ? "(required)" : "(optional)"}
        </label>
        <input
          id={`${idPrefix}-agreed-reason`}
          type="text"
          autoComplete="off"
          value={reason}
          onChange={(e) => onReason(e.target.value)}
          placeholder="e.g. fixed price agreed with the managing agent"
          // Session AEW review fix: the server bounds this at 500 characters. Without the same
          // bound here, a longer note produced a 400 whose field error this form does not
          // render, so the submit appeared to do nothing and the office was told nothing.
          maxLength={500}
          aria-invalid={reasonMissing}
          aria-describedby={reasonMissing ? reasonErrorId : undefined}
          className={`w-full rounded-ele border bg-ele-field px-3 py-2 outline-none focus:border-ele-green ${
            reasonMissing ? "border-ele-danger" : "border-ele-purple-accent"
          }`}
        />
        {reasonMissing ? (
          <p id={reasonErrorId} role="alert" className="mt-1 text-xs text-ele-danger">
            Enter a reason for the agreed price. It is recorded beside the standard rate so the figure is traceable.
          </p>
        ) : (
          <p className="mt-1 text-xs text-ele-body opacity-70">
            Recorded on the audit trail beside the standard rate, so an unusual figure is traceable.
            {reason.length > 400 ? ` ${500 - reason.length} characters left.` : ""}
          </p>
        )}
      </div>
    </>
  );
}

// The cost panel under each intake form. ONE component for all three forms (new customer, existing
// customer, emergency) so the agreed-price wording cannot drift between them.
//
// When an agreed price is entered it becomes the headline and the standard rate is still shown
// beside it, muted: the office should be able to see at a glance both what they agreed and what
// they overrode, rather than the computed figure simply vanishing.
function CalloutCostPanel({
  est,
  agreed,
  outOfHours,
  travelMins,
  footnote,
  extrasNote,
  accent,
}: {
  est: { bundled: boolean; firstHourNet: number; travelNet: number; totalNet: number; totalGross: number };
  agreed: AgreedPriceParse;
  outOfHours: boolean;
  travelMins: number;
  footnote: string; // the basis: true with or without an agreed price
  extrasNote: string; // what happens to additional time: true ONLY without an agreed price
  accent: "purple" | "green";
}) {
  const agreedNet = agreed.state === "ok" ? agreed.pence / 100 : null;
  const border = accent === "green" ? "border-ele-green" : "border-ele-purple-accent";
  const totalInk = accent === "green" ? "text-ele-green" : "text-ele-strong";
  return (
    <div className={`sm:col-span-2 rounded-ele border ${border} bg-ele-surface p-3 text-sm`}>
      {agreedNet != null ? (
        <>
          {/* Session AGZ sentence case: a full descriptive sentence, never forced caps. */}
          <div className="text-xs tracking-wide text-ele-body opacity-70">
            {est.bundled
              ? "Standard rate: bundled in the Complete tier"
              : `Standard rate: ${gbp(est.totalNet)} ex VAT${est.travelNet > 0 ? ` (first hour ${gbp(est.firstHourNet)}, travel ${gbp(est.travelNet)})` : ""}`}
          </div>
          {agreedNet > 0 ? (
            <div className={`mt-1 font-heading ${totalInk}`}>
              Agreed price: {gbp(agreedNet)} ex VAT ({gbp(Math.round(agreedNet * 1.2 * 100) / 100)} inc VAT)
            </div>
          ) : (
            <div className={`mt-1 font-heading ${totalInk}`}>Agreed free of charge</div>
          )}
          <div className="mt-1 text-xs text-ele-body opacity-70">
            {agreedNet > 0
              ? "This one-off agreed price replaces the standard rate on the draft invoice for this call-out. It does not change the customer's rates."
              : "No invoice will be raised for this call-out. It does not change the customer's rates."}
          </div>
        </>
      ) : est.bundled ? (
        <div className="text-ele-strong">Complete tier: bundled. No charge unless over the 6 annual call-out allowance.</div>
      ) : (
        <>
          <div className="flex flex-wrap justify-between gap-2">
            <span>First hour{outOfHours ? " (out of hours)" : ""}: {gbp(est.firstHourNet)}</span>
            <span>Travel{travelMins > 0 ? ` (${travelMins} mins)` : ""}: {gbp(est.travelNet)}</span>
          </div>
          <div className={`mt-1 font-heading ${totalInk}`}>
            Estimated total: {gbp(est.totalNet)} ex VAT ({gbp(est.totalGross)} inc VAT)
          </div>
        </>
      )}
      {/* Session AEW review fix, in two parts. The BASIS note is never suppressed: it says where
          the "Standard rate" above came from, and on the emergency form that this figure is what
          the payment link will charge. Suppressing it the instant a figure was typed removed the
          qualifier from the very number it qualifies.
          The EXTRAS note is suppressed under an agreed price, because it is no longer true:
          since the review fix the approval path HOLDS the additional-labour top-up on an
          agreed-price call-out rather than adding it to the draft. Saying "the office adds any
          additional hours" beside an agreed price would describe behaviour the platform no
          longer has. */}
      <div className="mt-1 text-xs text-ele-body opacity-70">
        {footnote}
        {agreedNet == null ? ` ${extrasNote}` : ""}
      </div>
    </div>
  );
}

// Tick cadence for the live clock below.
//
// THE RULE: tick as fast as the label actually changes, and no faster. formatSlaClock shows mm:ss
// under an hour and whole minutes above it, so the clock ticks per second exactly when a seconds
// digit is on screen, and every 30 seconds when it is not.
//
// This is deliberately keyed off the SAME one-hour boundary the formatter uses, not an independent
// window. An earlier version ticked per second only inside the last 2 minutes, which meant an
// entrapment (a 60-minute promise, the one clock the office genuinely watches) displayed a seconds
// digit that jumped in 30s steps for 58 of its 60 minutes: a clock that looks broken is worse than
// no clock, because the office stops trusting it.
//
// The waste this avoids is real but it lives at the other end: a 4-hour target would otherwise
// re-render 14,400 times per row to move a digit nobody is looking at. Above an hour the label only
// changes once a minute, so 30 seconds is already twice as often as it needs to be.
//
// A breached clock keeps running (the office wants to see how far past the promise it is) and
// follows the same rule, so a small overrun still counts up smoothly.
const SECONDS_SHOWN_BELOW_MS = 60 * 60 * 1000; // must match formatSlaClock's mm:ss threshold
const FAST_TICK_MS = 1000;
const SLOW_TICK_MS = 30 * 1000;

// The live clock label. Under an hour it keeps a live mm:ss feel (an entrapment's 60-minute promise
// is watched in seconds); at an hour or more it reads as hours, so a 4-hour target never renders the
// unreadable "SLA exceeded by 250:30". Pure.
function formatSlaClock(absMs: number): string {
  const totalSecs = Math.floor(absMs / 1000);
  if (totalSecs < 3600) {
    const mm = Math.floor(totalSecs / 60);
    const ss = totalSecs % 60;
    return `${mm}:${String(ss).padStart(2, "0")}`;
  }
  const totalMins = Math.floor(totalSecs / 60);
  const h = Math.floor(totalMins / 60);
  const m = totalMins % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

// A settled duration in plain English: 15 -> "15 min", 90 -> "1h 30m", 240 -> "4h". Mirrors the
// server's formatSlaMinutes (server/src/modules/sla/slaClock.ts) so the office reads one convention
// everywhere; duplicated rather than imported because server/ is not part of the SPA build. Pure.
function formatSlaDuration(mins: number): string {
  const m = Math.max(0, Math.round(mins));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem === 0 ? `${h}h` : `${h}h ${rem}m`;
}

// Session SC9A (one look for every state): the row's ONE state pill beside the call-out's own status word. An entrapment or emergency already
// carries its red pill in the Reference column and is "always first", so those rows get no second pill (a held one still says so in words).
// Otherwise the most urgent of: on hold (the dispatch job is held, with its reason), SLA held (the response clock is paused). Pure.
export function calloutState(c: Pick<CalloutListItem, "isEntrapment" | "isEmergency" | "holdReason" | "holdReasonText" | "slaOnHold" | "slaHeldLabel">): StatePillSpec | null {
  if (c.isEntrapment || c.isEmergency) return null;
  return primaryStatePill({
    status: c.holdReason ? "parts_pending" : undefined,
    holdReason: c.holdReason,
    holdReasonText: c.holdReasonText,
    slaHeldLabel: c.slaOnHold ? c.slaHeldLabel : null,
  });
}

// Live SLA countdown for a call-out (Session AK Item 10; widened from entrapments to EVERY call-out
// in Session ADQ, which gives every intake an attendance deadline rather than only the 60-minute
// entrapment promise).
//
// This component is deliberately dumb about judgement. It renders the clock the office needs while a
// call-out is running; it never decides met or breached. That judgement is server-side and
// persisted (server/src/modules/sla/slaClock.ts judgeSlaClock), because it has to survive the
// office closing the browser.
//
// The honesty rule, as it lands here:
//   - NO DEADLINE means the call-out is UNMEASURED: slaDeadline is only ever set when the intake
//     carried a real time of day, so a legacy row or a date-only log has none. Render nothing at
//     all: no "0:00", no fake pass, and no duration measured off a reportedAt that may be UTC
//     midnight.
//   - CANCELLED is never judged (mirroring judgeSlaClock), so the clock stops and shows nothing
//     rather than counting a dead call-out into an ever-deeper breach.
//   - RESOLVED shows how long it took WITHOUT calling it a pass (see the note on that branch).
//   - ASSIGNED (Session ATC, D18) stops the clock: the response leg is logged to an engineer assigned,
//     so the countdown is the time left to ASSIGN one, and once assigned it states how that landed
//     against the deadline, neutrally, from the two recorded instants.
//   - HELD (Session AUT): while an SLA hold is open the clock is paused, so it shows "SLA held: <reason>" and no
//     countdown; once released it counts down to the EFFECTIVE due instant (the intake deadline plus the time held),
//     which the server sends as slaDueAt.
export function SlaCountdown({
  deadline,
  completedAt,
  reportedAt,
  status,
  assignedAt = null,
  dueAt,
  onHold = false,
  heldLabel = null,
}: {
  deadline: string | null;
  completedAt: string | null;
  reportedAt: string | null;
  status?: string | null;
  assignedAt?: string | null;
  // Session AUT: the effective due instant when the server sent one (absent on an older cache: the deadline stands).
  dueAt?: string | null;
  onHold?: boolean;
  heldLabel?: string | null;
}) {
  const [now, setNow] = useState(() => Date.now());

  const effective = dueAt !== undefined && dueAt !== null ? dueAt : deadline;
  const deadlineMs = effective ? new Date(effective).getTime() : NaN;
  const remainingMs = Number.isNaN(deadlineMs) ? null : deadlineMs - now;
  const assignedMs = assignedAt ? new Date(assignedAt).getTime() : NaN;
  // The clock runs only while there is something to count: an unresolved, uncancelled call-out that
  // has an honest deadline and nobody assigned yet.
  const running = remainingMs != null && !completedAt && status !== "cancelled" && Number.isNaN(assignedMs) && !onHold;
  // Tick per second exactly while a seconds digit is on screen (either side of the deadline).
  const tickMs = remainingMs != null && Math.abs(remainingMs) < SECONDS_SHOWN_BELOW_MS ? FAST_TICK_MS : SLOW_TICK_MS;

  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => setNow(Date.now()), tickMs);
    return () => clearInterval(t);
  }, [running, tickMs]);

  if (status === "cancelled") return null;
  // Session AUT: paused while the call-out waits on the customer. Never hidden, never counted down.
  if (onHold && !completedAt && Number.isNaN(assignedMs)) {
    return (
      <div className="font-menu text-[10px] font-bold tracking-wide text-ele-body" title="The response clock is paused while we wait on the customer.">
        {heldLabel ?? "SLA held"}
      </div>
    );
  }
  if (remainingMs == null) return null; // unmeasured: nothing honest to show

  if (!Number.isNaN(assignedMs)) {
    // The response leg stopped when the engineer was assigned. Neutral, like the closed line below:
    // the authoritative verdict is the server-judged response outcome on the call-out's own page.
    const deltaMins = Math.round(Math.abs(deadlineMs - assignedMs) / 60000);
    const inside = assignedMs <= deadlineMs;
    return (
      <div
        className="font-menu text-[10px] tracking-wide text-ele-body"
        title="The response clock runs from logging the call-out to an engineer being assigned. The engineer's travel is not part of it."
      >
        Engineer assigned, {formatSlaDuration(deltaMins)} {inside ? "inside" : "after"} the target
      </div>
    );
  }

  if (completedAt) {
    // The elapsed time from the call being reported to the office pressing Complete. That is NOT an
    // SLA outcome (completedAt is office admin latency, not the engineer's fix), so this line is
    // deliberately neutral: no green, which is the platform's pass colour, and no met / breached
    // claim. The authoritative verdict is the server-judged slaAttendanceOutcome, which this list
    // does not carry.
    const start = reportedAt ? new Date(reportedAt).getTime() : NaN;
    const end = new Date(completedAt).getTime();
    const mins = Number.isNaN(start) || Number.isNaN(end) ? null : Math.max(0, Math.round((end - start) / 60000));
    return (
      <div
        className="font-menu text-[10px] tracking-wide text-ele-body"
        title="Time from the call being reported to the office marking it complete. This is not the response time and not the SLA result."
      >
        Closed{mins != null ? ` in ${formatSlaDuration(mins)}` : ""}
      </div>
    );
  }

  const exceeded = remainingMs < 0;
  const label = formatSlaClock(Math.abs(remainingMs));
  return (
    <div
      className={`font-menu text-[10px] font-bold tracking-wide ${exceeded ? "text-ele-danger" : "text-amber-400"}`}
      title="Time left to assign an engineer (the response target)."
    >
      {exceeded ? `SLA exceeded by ${label}` : `SLA ${label} left`}
    </div>
  );
}

type FormMode = null | "log" | "emergency";
// The log form defaults to NOW (date and time), not today's date: the response clock starts at
// reportedAt, and a date-only value parses to UTC midnight, which would make the measured response
// fiction (Session ADQ).
const emptyLog = { customerId: "", siteId: "", assetId: "", faultDescription: "", reportedAt: nowLocalInput() };
const emptyEmergency = { customerName: "", customerEmail: "", customerPhone: "", siteAddress: "", faultDescription: "", reportedAt: nowLocalInput() };
// The inline "New customer" fields (Session AEP): the smallest set that makes a real, lasting
// Customer + Site + Asset. Only the three names are required; a walk-in has no ELE number yet
// (numbers are issued at signing) and may not know the lift type.
const emptyNewCustomer = { customerName: "", customerEmail: "", customerPhone: "", siteName: "", siteAddress: "", eleNumber: "", liftType: "" };

// "Log this emergency call-out?" branded confirm (Session AKA), closing the Session AEH gap the
// intake button had (a plain submit, no confirm of any kind). Shown BEFORE the call-out is raised:
// Cancel goes back to the form untouched; "Log only" and "Log and notify now" both raise the SAME
// call-out, differing only in whether the confirmation is sent for real or left as the AJI
// Gmail-draft-for-review default.
// Session ASW (Director decision D22): how a caller-check signal reads to the office.
const CALLER_MATCH_SIGNAL: Record<string, string> = { email: "email address", phone: "phone number", site_postcode: "site postcode", name: "name" };

// Session ASW (D22): what the always-on caller check found, and the office's case-by-case choice to bill this one
// attendance at a matched customer's contract rate (the in-contract Core card). Nothing is ticked by default.
export function CallerMatchPanel({
  check,
  applyId,
  onApply,
}: {
  check: CallerMatchResult | "loading" | "error" | null;
  applyId: string | null;
  onApply: (id: string | null) => void;
}) {
  if (check === null) return null;
  if (check === "loading") return <p className="mt-3 text-xs text-ele-body opacity-80">Checking the caller against existing customers...</p>;
  if (check === "error" || check.failed) {
    return (
      <p className="mt-3 text-xs text-ele-body opacity-80">
        The customer check could not run just now. It runs again when the call-out is logged; the non-contract rate applies.
      </p>
    );
  }
  if (check.candidates.length === 0) {
    return <p className="mt-3 text-xs text-ele-body opacity-80">Checked against existing customers: no match. The non-contract rate applies.</p>;
  }
  return (
    <div className="mt-3 rounded-ele border border-amber-500 bg-amber-500/10 p-3" role="status">
      <p className="font-menu text-xs uppercase tracking-wide text-amber-400">Possible existing customer</p>
      <ul className="mt-1 space-y-2 text-sm">
        {check.candidates.map((c) => {
          const why = c.matchedOn.map((m) => CALLER_MATCH_SIGNAL[m] ?? m).join(", ");
          const site = c.siteName ? `, site ${c.siteName}` : "";
          const cover = c.activeTiers.length > 0 ? `; ${c.activeTiers.join(" and ")} contract` : "; no active contract";
          return (
            <li key={c.customerId}>
              <span className="text-ele-strong">{c.customerName}</span>
              <span className="text-ele-body">{` (matched on ${why}${site}${cover})`}</span>
              {/* Session ASW (review L2): a name or a postcode alone is a weak signal; say so. */}
              {c.matchedOn.length === 1 && (c.matchedOn[0] === "name" || c.matchedOn[0] === "site_postcode") && (
                <span className="ml-1 text-xs text-amber-400">Weak match: check with the caller.</span>
              )}
              {/* Session SC9B: a matched customer on stop is a warning for the office, never a reason to hold an emergency. */}
              <AccountOnStopLine customerId={c.customerId} urgent />
              {c.contractRateAvailable && (
                <label className="mt-1 flex items-start gap-2 text-sm text-ele-body">
                  <input
                    type="checkbox"
                    checked={applyId === c.customerId}
                    onChange={(e) => onApply(e.target.checked ? c.customerId : null)}
                    className="mt-0.5"
                  />
                  <span>{`This caller is ${c.customerName}: bill this attendance at their contract rate (the in-contract Core rate) instead of the non-contract rate.`}</span>
                </label>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function EmergencyConfirmModal({
  open,
  caller,
  email,
  phone,
  saving,
  onCancel,
  onLogOnly,
  onLogAndNotify,
  callerCheck,
  applyMatchId,
  onApplyMatch,
  priceLine,
}: {
  open: boolean;
  caller: string;
  email: string;
  phone: string;
  saving: boolean;
  onCancel: () => void;
  onLogOnly: () => void;
  onLogAndNotify: () => void;
  callerCheck?: CallerMatchResult | "loading" | "error" | null;
  applyMatchId?: string | null;
  onApplyMatch?: (id: string | null) => void;
  // Session ASW (D22, review M1): the figure the caller will actually be quoted, which moves with the matched-rate
  // tick, so a "Log and notify now" never sends a price the office did not see.
  priceLine?: string | null;
}) {
  if (!open) return null;
  const who = caller || "the caller";
  return (
    <ModalShell ariaLabel="Log emergency call-out" onClose={() => !saving && onCancel()} closeOnBackdrop width="max-w-md" panelClassName="p-6 text-left">
      <h2 className="mb-2 font-heading text-xl text-ele-strong">Log this emergency call-out?</h2>
      <p className="text-sm text-ele-body">
        This raises the call-out for <span className="text-ele-strong">{who}</span> and asks whether to send{" "}
        {phone ? "an email and a text" : "an email"} to {email}
        {phone ? ` (${phone})` : ""} now with their job reference and the Pay now link, or leave it as a draft for you to review first.
      </p>
      <CallerMatchPanel check={callerCheck ?? null} applyId={applyMatchId ?? null} onApply={(id) => onApplyMatch?.(id)} />
      {priceLine && <p className="mt-3 text-sm text-ele-strong">{priceLine}</p>}
      <div className={`mt-5 gap-2 ${MODAL_ACTIONS}`}>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body disabled:opacity-50 ${MODAL_ACTION_BTN}`}
        >
          Cancel
        </button>
        <Button variant="destructive" onClick={onLogOnly} disabled={saving} className={MODAL_ACTION_BTN}>
          {saving ? "Logging..." : "Log only"}
        </Button>
        <Button onClick={onLogAndNotify} disabled={saving} className={MODAL_ACTION_BTN}>
          {saving ? "Logging..." : "Log and notify now"}
        </Button>
      </div>
    </ModalShell>
  );
}

export function CalloutsPage() {
  // Session SC2C: the page of call-outs the SERVER answered for the current filters, sort and page, and the true total of every match.
  const [callouts, setCallouts] = useState<CalloutListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [pageState, setPageState] = useState<{ key: string; offset: number }>({ key: "", offset: 0 });
  const [refreshing, setRefreshing] = useState(false);
  const listSeq = useRef(0);
  // Card list below md, table otherwise -- decided once at mount (Session AHB's mechanism, the
  // same one InvoicesPage etc. use). jsdom has no matchMedia, so every existing test still
  // renders the table it pins.
  const [mobileCards] = useState(initialMobileLayout);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Session AQY (D-AQX-4): the row whose CancelJobModal is open (stashed whole, not just the id, so
  // the modal can build its own "ELE-xxx | Site" label). Which statuses (cancelled included) are
  // currently visible is the Session AMT filter bar's job, not a page-local toggle.
  const [cancellingCallout, setCancellingCallout] = useState<CalloutListItem | null>(null);
  const [completingId, setCompletingId] = useState<string | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);

  // Hard delete (admin only): a confirmation modal targets one call-out, then scrubs
  // its DRAFT Xero invoice + Stripe link.
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // Session ARA (S-7), D57: dispatching an engineer to a call-out is now admin AND office (the
  // Director's 23 September 2026 ruling); isAdmin above stays admin-only for Delete, which D57 never
  // touched. See calloutRowActions.ts for why the two are kept as separate fields.
  const canDispatch = user?.role === "admin" || user?.role === "office";
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; ref: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteResult, setDeleteResult] = useState<string | null>(null);

  // The call-out whose charge panel is open (Session W). Computing/saving a charge
  // reloads the list so any new invoice/status shows.
  const [chargeTarget, setChargeTarget] = useState<CalloutListItem | null>(null);
  // The call-out whose photo gallery is open (a completed visit's photos).
  const [photoTarget, setPhotoTarget] = useState<CalloutListItem | null>(null);
  // Session AEY: the Call-Out Attendance Report workflow. reportOpen toggles the modal; reportInitial
  // pre-fills it from an existing call-out (null = a fresh standalone report). reportLoadingId shows
  // a brief loading state on the row while the detail (for the stored report form) is fetched.
  const [reportOpen, setReportOpen] = useState(false);
  const [reportInitial, setReportInitial] = useState<CalloutReportInitial | null>(null);
  const [reportLoadingId, setReportLoadingId] = useState<string | null>(null);
  // Session AHJ: the call-out whose issued report reference is being corrected (null = closed).
  const [refTarget, setRefTarget] = useState<CalloutListItem | null>(null);
  // Session AJW; renamed Session AQY (D-AQX-3): which row's "Release" is in flight (busy state on
  // that one row's button only).
  const [sendingReportId, setSendingReportId] = useState<string | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const [mode, setMode] = useState<FormMode>(null);
  // Session ANV: the logged inbound call this call-out is being raised from, when the office arrived
  // here from the Call log's "Raise work". Read ONCE from the incoming link (below) and held for the
  // life of the page, because the effect that reads it strips the query string immediately so a
  // refresh does not silently re-link a second call-out to the same call. Null on every other visit,
  // which is what keeps the dashboard's own "New call-out" byte-identical to its pre-ANV behaviour.
  const [fromCallLogId, setFromCallLogId] = useState<string | null>(null);
  // Session ARE (D82): the Enquiry sibling of fromCallLogId above (src/routes/office/
  // EnquiryDetailPage.tsx's own "Raise work" -> onCallout hand-off), same reasoning and the same
  // read-once-then-strip lifetime.
  const [fromEnquiryId, setFromEnquiryId] = useState<string | null>(null);

  const [logForm, setLogForm] = useState({ ...emptyLog });
  const [emForm, setEmForm] = useState({ ...emptyEmergency });
  // Inline "New customer" mode (Session AEP). The office picks an existing customer from the
  // cascade, or creates a real Customer + Site + Asset here and logs the call-out straight against
  // it. This is the lasting-record path; the Emergency panel remains the right tool for a genuinely
  // one-off caller nobody wants a record for.
  const [logCustomerMode, setLogCustomerMode] = useState<"existing" | "new">("existing");
  const [newForm, setNewForm] = useState({ ...emptyNewCustomer });
  // The resumable Customer -> Site -> Asset chain (Session AOI: extracted into a shared hook so its
  // retry-safety cannot drift between this page and RaiseWorkModal.tsx's own "lift not on the
  // register yet" flow). `chain.created`/`chain.chainFailure` replace this page's former local
  // `created`/`chainFailure` state one for one; every call site below reads/writes through `chain`.
  const chain = useNewCustomerChain();
  // Entrapment (Session AK Item 10; Session ACY: folded into the Log call-out form as a toggle).
  // Reuses the log cascade (logForm customer/site/lift) plus a caller name. When the toggle is on,
  // submitting the log form dispatches an entrapment (60-minute SLA) rather than a routine call-out.
  const [logIsEntrapment, setLogIsEntrapment] = useState(false);
  const [entCaller, setEntCaller] = useState("");
  const [entResult, setEntResult] = useState<string | null>(null);
  // Feedback after a routine (non-entrapment) call-out is logged: the form closes, so without this
  // the office got no confirmation the call-out was created (Session AEH, FIX-2 B3).
  const [logResult, setLogResult] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [emergencyResult, setEmergencyResult] = useState<(EmergencyCalloutResult & { email: string; phone: string }) | null>(null);
  // The "Log this emergency call-out?" branded confirm (Session AKA), shown BEFORE the call-out is
  // raised: it names the caller and what is about to be sent, closing the Session AEH gap the
  // intake button had (a plain submit, no confirm step of any kind).
  const [emergencyConfirmOpen, setEmergencyConfirmOpen] = useState(false);
  // Session ASW (D22): the caller check shown on the confirm, and the office's choice to apply a matched contract rate.
  const [callerCheck, setCallerCheck] = useState<CallerMatchResult | "loading" | "error" | null>(null);
  const [applyMatchId, setApplyMatchId] = useState<string | null>(null);
  const callerCheckSeq = useRef(0);

  // Out-of-hours toggle + travel time (mins) for each form's rate/estimate.
  const [logOoh, setLogOoh] = useState(false);
  const [logTravel, setLogTravel] = useState(0);
  // Session AEW: the one-off price agreed with the customer for THIS call-out, and why. Held as
  // the RAW typed string (not a number) so an empty field stays empty and a half-typed "2." is
  // not silently reinterpreted; parseAgreedPrice turns it into pence at the boundary.
  const [logAgreedPrice, setLogAgreedPrice] = useState("");
  const [logAgreedReason, setLogAgreedReason] = useState("");
  // The log estimate is tier-dependent; the office picks the contract tier for the
  // estimate (the invoice itself uses the contract's actual tier, server-side).
  const [logTier, setLogTier] = useState<CalloutTier>("core");
  const [emOoh, setEmOoh] = useState(false);
  const [emTravel, setEmTravel] = useState(0);
  const [emAgreedPrice, setEmAgreedPrice] = useState("");
  const [emAgreedReason, setEmAgreedReason] = useState("");

  // Re-read the page the person is on (after an action changes a row). The query, offset and fetcher are the ones the effect uses.
  async function reloadCallouts() {
    await fetchPage(pageQuery, offset);
  }

  // Session AQY (D-AQX-4): the simple two-step ConfirmButton (Session AEH's own note above describes
  // that pre-AQY confirm step) was replaced by CancelJobModal, which owns its own two-step confirm
  // and additionally asks whether the engineer had already set off or arrived. When this call-out has
  // an open dispatch job, the route also cancels IT atomically and accepts the optional
  // "attended, then called off" travel record -- see the modal render below.

  // Assign an engineer (admin): a modal with the active engineers; on assign the call-out
  // moves to in_progress and a linked job appears on that engineer's PWA.
  const [assignTarget, setAssignTarget] = useState<CalloutListItem | null>(null);
  const [engineers, setEngineers] = useState<AssignableEngineer[]>([]);
  const [selectedEngineer, setSelectedEngineer] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);

  // Session AMS: an engineer picked straight on the Log call-out form (admin only), so the office
  // can log and dispatch in one action instead of logging, then finding the row, then Assign.
  // Reuses this SAME engineers list and the SAME assign action the row's Assign button calls below
  // -- never a second engineer source, and never a combined endpoint (the create and assign routes
  // have no shared transaction, so this is two calls chained from the form, not one). Not offered
  // on the entrapment sub-form: entrapment already alerts every active engineer immediately, so
  // singling one out here would contradict that broadcast rather than add to it.
  const [logEngineerId, setLogEngineerId] = useState("");
  const [engineerListError, setEngineerListError] = useState<string | null>(null);

  function openAssign(c: CalloutListItem) {
    setAssignTarget(c);
    setSelectedEngineer("");
    setAssignError(null);
    // Load the active engineers once (best-effort; the modal shows an error if empty).
    if (engineers.length === 0) {
      api.callouts
        .engineers()
        .then(setEngineers)
        .catch(() => setAssignError("Could not load the engineer list. Please try again."));
    }
  }

  // Session SC5: Accept on the proposed engineer passes that engineer here; the picker below still passes the chosen one. One route either way.
  async function doAssign(engineerId: string = selectedEngineer) {
    if (!assignTarget || !engineerId) return;
    setAssigning(true);
    setAssignError(null);
    try {
      const updated = await api.callouts.assign(assignTarget.id, engineerId);
      setCallouts((cs) => cs.map((c) => (c.id === updated.id ? updated : c)));
      setAssignTarget(null);
    } catch (err) {
      setAssignError(
        err instanceof ApiError && err.status === 409
          ? "This call-out can no longer be assigned."
          : "Could not assign the engineer. Please try again.",
      );
    } finally {
      setAssigning(false);
    }
  }

  // The confirm step is the two-step arming on the row's Complete button (ConfirmButton), not a
  // native dialog (Session AEH, FIX-1 A1). The NPS-survey note is scoped to emergency call-outs on
  // that button, since a routine call-out does not trigger a survey.
  async function completeCallout(id: string) {
    setCompletingId(id);
    setCancelError(null);
    try {
      const updated = await api.callouts.complete(id);
      setCallouts((cs) => cs.map((c) => (c.id === id ? updated : c)));
    } catch {
      setCancelError("Could not complete that call-out. Please try again.");
    } finally {
      setCompletingId(null);
    }
  }

  // Session AJW Item 2; rebuilt Session AQY (D-AQX-3): RELEASE the filed attendance report PDF --
  // opens portal visibility and drafts (never sends) the completion email and, where eligible, the
  // NPS survey. Single-fire (the route no-ops with alreadyReleased on a repeat call); reuses the
  // same shared row-error banner completeCallout already writes to, rather than a new error slot for
  // one more row action.
  async function releaseReport(id: string) {
    setSendingReportId(id);
    setCancelError(null);
    try {
      const res = await api.callouts.releaseReport(id);
      setCallouts((cs) => cs.map((c) => (c.id === id ? { ...c, reportSentAt: res.releasedAt, reportSentTo: res.releasedTo } : c)));
    } catch (err) {
      setCancelError(friendlyApiError(err, "Could not release the report. Please try again."));
    } finally {
      setSendingReportId(null);
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const summary: ScrubSummary = await api.callouts.delete(deleteTarget.id);
      setCallouts((cs) => cs.filter((c) => c.id !== deleteTarget.id));
      const parts = [
        `Xero draft ${summary.xeroVoided ? "deleted" : "not removed"}`,
        `Stripe link ${summary.stripeDeleted ? "removed" : "not removed"}`,
      ];
      setDeleteResult(`Call-out ${deleteTarget.ref} deleted. ${parts.join("; ")}.`);
      setDeleteTarget(null);
    } catch (err) {
      setDeleteError(friendlyApiError(err, "Could not delete the call-out. Please try again."));
    } finally {
      setDeleting(false);
    }
  }

  // Session AMT: the filters and sort live in the URL query string, so a reload or a shared link reproduces the same view. Session SC2C: the
  // server applies them (calloutPageQuery); the pure parsing stays in calloutListFilters.ts.
  const filterState = useMemo(() => parseCalloutFilters(searchParams), [searchParams]);
  const sortState = useMemo(() => parseSortState(searchParams), [searchParams]);
  const debouncedQ = useDebouncedValue(filterState.q, 250);
  const pageQuery = useMemo(() => calloutPageQuery(filterState, sortState, debouncedQ), [filterState, sortState, debouncedQ]);

  // Session SC2C: ask the server for ONE PAGE (50) of the call-outs matching the filters and sort in the URL, with the true total. Typing
  // in the search box waits for a pause (250 ms) before asking; any change of filter or sort goes back to the first page.
  async function fetchPage(query: CalloutPageQuery, at: number): Promise<void> {
    const mine = ++listSeq.current;
    if (query.empty) {
      setCallouts([]);
      setTotal(0);
      return;
    }
    const { empty: _empty, ...asked } = query;
    const res = await api.callouts.listPage({ ...asked, limit: CALLOUT_PAGE_SIZE, offset: at });
    if (mine !== listSeq.current) return;
    setCallouts(res.items);
    setTotal(res.total);
  }

  // Session SC2B: this page loads the call-outs and nothing else. Its rows already carry the site, customer and lift names; the Log
  // call-out form and the report modal SEARCH customers, sites and lifts (typeahead pickers) instead of holding all three registers.
  const queryKey = JSON.stringify(pageQuery);
  const offset = pageState.key === queryKey ? pageState.offset : 0;
  const firstLoad = useRef(true);
  useEffect(() => {
    let live = true;
    if (firstLoad.current) setLoading(true);
    else setRefreshing(true);
    setError(false);
    fetchPage(pageQuery, offset)
      .catch(() => {
        if (live) setError(true);
      })
      .finally(() => {
        if (!live) return;
        firstLoad.current = false;
        setLoading(false);
        setRefreshing(false);
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey, offset]);

  // The dashboard quick actions link here with ?new=log or ?new=emergency.
  //
  // Session ANV adds a third caller: the Call log's "Raise work", which links here with
  // ?new=log&callLogId=...&customerId=..., so the office lands on the Log call-out form with the
  // caller already chosen and the call carried through to the create. The CUSTOMER is preselected
  // and nothing else is: a call log holds no site and no lift, so the form's own cascade -- site,
  // then the lift on that site, then the fault -- is exactly the Director's steps 2, 3 and 5, and it
  // already walked them before this session existed.
  useEffect(() => {
    const which = searchParams.get("new");
    const callLogId = searchParams.get("callLogId");
    // Session ARE (D82): the Enquiry review queue's own "Raise work" hand-off, mirroring callLogId
    // above exactly (same query-string shape, same read-once-then-strip lifetime, same intent --
    // the office lands here with the caller's customer already chosen).
    const enquiryId = searchParams.get("enquiryId");
    const fromCustomerId = searchParams.get("customerId");
    // Session ASM (W-12): "Raise work" on a site or lift page links here with the site and, from a lift, the lift too,
    // so the office lands on the ordinary Log call-out form with the whole cascade already chosen. Only ever applied
    // on top of a customer, since the form cascades customer -> site -> lift.
    const fromSiteId = fromCustomerId ? searchParams.get("siteId") : null;
    const fromAssetId = fromSiteId ? searchParams.get("assetId") : null;
    if (callLogId) setFromCallLogId(callLogId);
    if (enquiryId) setFromEnquiryId(enquiryId);
    if (which === "log") openLog();
    else if (which === "emergency") openEmergency();
    // ?new=entrapment opens the Log call-out form with the entrapment toggle already on. The
    // dashboard's separate Emergency button was retired (Session ACY); the deep link still works.
    else if (which === "entrapment") openLog(true);
    // AFTER openLog, never before: openLog resets logForm to emptyLog, so a customer chosen first
    // would be wiped by the very call meant to show it. onCustomer's own update is functional, so
    // running it second lands on top of that reset instead of under it.
    if (fromCustomerId) onCustomer(fromCustomerId);
    // Session ASM: after onCustomer, which clears the site and lift; both updates are functional, so they land in order.
    if (fromSiteId) onSite(fromSiteId);
    if (fromAssetId) setLogForm((f) => ({ ...f, assetId: fromAssetId }));
    if (which || callLogId || enquiryId || fromCustomerId) {
      const next = new URLSearchParams(searchParams);
      next.delete("new");
      // Session ANV: stripped along with `new`, and for a sharper reason than tidiness. Left in the
      // URL, a refresh or a shared link would re-enter this page still believing it was raising work
      // off that call, and the office's SECOND, unrelated call-out of the day would be recorded
      // against it. The page's own state is the one home of the link from here on.
      next.delete("callLogId");
      // Session ARE: stripped for the identical reason, same enquiry.
      next.delete("enquiryId");
      next.delete("customerId");
      next.delete("siteId");
      next.delete("assetId");
      setSearchParams(next, { replace: true });
    }
    // run once for the incoming link
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Whether the SELECTED lift is on a contract, from the register row the picker returned (its own hasContract flag). null when
  // no lift is picked yet, the lift came in by link (no row), or the flag is not on the row: in that case the
  // estimate keeps its long-standing in-contract assumption rather than guessing the dearer rate, until the server's own
  // billing basis for the lift (below) answers.
  const [pickedLiftCovered, setPickedLiftCovered] = useState<{ assetId: string; covered: boolean | null } | null>(null);
  const selectedLiftCovered: boolean | null = logForm.assetId && pickedLiftCovered?.assetId === logForm.assetId ? pickedLiftCovered.covered : null;

  // Session AVJ (C3-038): the server's own billing basis for the picked lift. It carries the 30-day
  // lapse grace that hasContract cannot, so a lift whose contract ended recently is estimated the way
  // it will be billed (one home for the rule, RULE 6). Keyed by lift id so a stale answer for an
  // earlier pick is never used; a failed read leaves the register flag standing.
  const [liftBasis, setLiftBasis] = useState<{ assetId: string; inContract: boolean; terminatedFor: string | null } | null>(null);
  useEffect(() => {
    const id = logForm.assetId;
    if (!id) return;
    let live = true;
    api.callouts
      .billingBasis(id)
      .then((b) => { if (live) setLiftBasis({ assetId: id, inContract: b.inContract, terminatedFor: b.terminatedForCause?.text ?? null }); })
      .catch(() => { if (live) setLiftBasis(null); });
    return () => { live = false; };
  }, [logForm.assetId]);
  const liftInContract: boolean | null =
    logForm.assetId && liftBasis?.assetId === logForm.assetId ? liftBasis.inContract : selectedLiftCovered;

  function openLog(asEntrapment = false) {
    // A chain that committed records is NEVER silently discarded. Reopening the form would
    // otherwise clear `created`, taking the panel and its links with it, and the office would
    // retype the name and create a SECOND customer with the first left unmentioned on the
    // register. So an unfinished chain is resumed, not reset: only the call-out fields are
    // cleared, and the mode toggle's own guard keeps it in New customer mode.
    const resuming = chain.created.customer != null;
    setLogForm({ ...emptyLog });
    setLogOoh(false);
    setLogTravel(0);
    setLogAgreedPrice("");
    setLogAgreedReason("");
    setLogTier("core");
    setLogIsEntrapment(asEntrapment);
    setEntCaller("");
    setLogEngineerId("");
    setEngineerListError(null);
    setFieldErrors({});
    setFormError(null);
    setEmergencyResult(null);
    setEntResult(null);
    setLogResult(null);
    if (!resuming) {
      setLogCustomerMode("existing");
      setNewForm({ ...emptyNewCustomer });
      chain.reset();
    }
    // Admin and office (Session ASM, the D57 rollout gap: the same canDispatch gate the row-level Assign button has
    // used since Session ARA), and cached in the same `engineers` array that button already loads into -- opening this
    // form after assigning a row (or vice versa) never re-fetches.
    if (canDispatch && engineers.length === 0) {
      api.callouts
        .engineers()
        .then(setEngineers)
        .catch(() =>
          setEngineerListError("Could not load the engineer list. You can still log the call-out and assign an engineer afterwards from the list below."),
        );
    }
    setMode("log");
  }
  function openEmergency() {
    setEmForm({ ...emptyEmergency });
    setEmOoh(false);
    setEmTravel(0);
    setEmAgreedPrice("");
    setEmAgreedReason("");
    setFieldErrors({});
    setFormError(null);
    setEmergencyResult(null);
    setMode("emergency");
  }
  function closeForm() {
    setMode(null);
    setFieldErrors({});
    setFormError(null);
  }

  // Session AEY: open the Call-Out Attendance Report as a fresh standalone report.
  function openNewReport() {
    setReportInitial(null);
    setReportOpen(true);
  }
  // Open the report pre-filled from an existing call-out. Fetches the detail for a stored report
  // form (regenerate) + the saved PDF link; falls back to the call-out's basics from the list row.
  async function openReport(c: CalloutListItem) {
    setReportLoadingId(c.id);
    // Basics from the list row, used when the detail fetch fails. The lift's type, make, serial and the site address are on the call-out's
    // DETAIL (Session SC2B), so a failed fetch leaves those blank for the office to type, exactly as an unreadable register did.
    const isOneOff = !c.customerId;
    const basicForm: CalloutReportInitial["form"] = {
      calloutId: c.id,
      customerMode: isOneOff ? "oneoff" : "existing",
      customerId: c.customerId,
      siteId: c.siteId,
      assetId: c.assetId,
      siteName: isOneOff ? "" : c.siteName === "No site linked" ? "" : c.siteName,
      siteAddress: "",
      liftDesignation: c.assetRef ?? "",
      liftType: "",
      manufacturer: "",
      serialNumber: "",
      controllerDrive: "",
      faultReported: c.faultDescription ?? "",
      dateReported: c.reportedAt ? c.reportedAt.slice(0, 10) : undefined,
      dateAttended: c.attendedAt ? c.attendedAt.slice(0, 10) : undefined,
    };
    try {
      const d = await api.callouts.get(c.id);
      setReportInitial(buildReportInitial(d, await storedFormSiteId(d)));
    } catch {
      setReportInitial({ calloutId: c.id, form: basicForm });
    } finally {
      setReportLoadingId(null);
      setReportOpen(true);
    }
  }

  function onCustomer(customerId: string) {
    setLogForm((f) => ({ ...f, customerId, siteId: "", assetId: "" }));
  }
  function onSite(siteId: string) {
    setLogForm((f) => ({ ...f, siteId, assetId: "" }));
  }

  // Session AEW: the typed agreed price, parsed once per render and shared by the estimate panel,
  // the submit guard and the request body, so the figure the office is SHOWN is provably the
  // figure that gets sent. An entrapment carries no agreed price (it is dispatched first and
  // priced from the visit), so its field is not rendered and its parse is forced blank.
  const logAgreed = logIsEntrapment ? ({ state: "blank", pence: null } as const) : parseAgreedPrice(logAgreedPrice);
  const emAgreed = parseAgreedPrice(emAgreedPrice);
  // Session AFO: an agreed price requires a reason (matching the quote manual price override). When a
  // valid figure is set (state "ok", which includes an agreed zero), a blank reason blocks the submit
  // just as an unparseable price does. The server enforces the same rule (a 400 on agreedPriceReason),
  // so this is the client half of a defence in depth, not the only gate.
  const logAgreedReasonMissing = logAgreed.state === "ok" && logAgreedReason.trim().length === 0;
  const emAgreedReasonMissing = emAgreed.state === "ok" && emAgreedReason.trim().length === 0;

  async function submitLog(e: FormEvent) {
    e.preventDefault();
    // In "New customer" mode the ConfirmButton is the only way to fire this: creating a customer,
    // dispatching an engineer and raising an invoice is not something a stray Enter key should do.
    if (logCustomerMode === "new") return;
    await runLogSubmit();
  }

  async function runLogSubmit() {
    setSaving(true);
    setFieldErrors({});
    setFormError(null);
    setLogResult(null);
    try {
      // New customer: commit the records first, then fall through to the SAME dispatch and
      // invoicing path an existing customer takes. Nothing about the call-out itself is forked.
      let assetId = logForm.assetId;
      if (logCustomerMode === "new") {
        // No customerType: this new call-out customer keeps taking the schema default (CONTRACT),
        // byte-identical to this page's behaviour before the chain moved into a shared hook.
        const newAssetId = await chain.run({
          customerName: newForm.customerName,
          customerEmail: newForm.customerEmail,
          customerPhone: newForm.customerPhone,
          siteName: newForm.siteName,
          siteAddress: newForm.siteAddress,
          eleNumber: newForm.eleNumber,
          liftType: newForm.liftType,
        });
        if (!newAssetId) return; // chain.chainFailure explains what landed and what did not
        assetId = newAssetId;
      }
      if (logIsEntrapment) {
        // The entrapment path: dispatch immediately on the 60-minute SLA. No rate/estimate; any
        // charge is computed later from the visit, exactly as the standalone entrapment form did.
        await api.callouts.entrapment({
          assetId,
          callerName: entCaller.trim() || undefined,
          description: logForm.faultDescription.trim() || undefined,
          // Session ANV: an entrapment reaches this route through the same form, so it carries the
          // call too. Omitted when there is none, exactly as the ordinary path above.
          ...(fromCallLogId ? { callLogId: fromCallLogId } : {}),
          // Session ARE (D82): the enquiry sibling of callLogId above. `enquiryId` is not yet a
          // declared field on api.callouts.entrapment's request body (src/lib/api.ts, owned by the
          // parallel backend session -- not edited here); spread in rather than widening that
          // signature, mirroring how RaiseWorkModal.tsx merges the same field in.
          ...(fromEnquiryId ? { enquiryId: fromEnquiryId } : {}),
        });
        setEntResult(
          "Entrapment logged. The office and active engineers have been alerted, and the 60-minute response clock has started." +
            (fromCallLogId ? " The call it came from is recorded against it." : "") +
            (fromEnquiryId ? " The enquiry it came from is recorded against it." : ""),
        );
      } else {
        const logged = await api.callouts.create({
          assetId,
          reportedAt: logForm.reportedAt,
          faultDescription: logForm.faultDescription.trim() || undefined,
          outOfHours: logOoh,
          travelMins: logTravel,
          // Session AEW. Sent ONLY when the office typed a valid figure: an omitted key is what
          // makes the no-agreed-price path byte-identical to the pre-AEW behaviour, and an
          // unparseable field has already blocked the submit above.
          ...(logAgreed.state === "ok"
            ? { agreedPricePence: logAgreed.pence, ...(logAgreedReason.trim() ? { agreedPriceReason: logAgreedReason.trim() } : {}) }
            : {}),
          // Session ANV: the logged call this came from, when the office arrived from the Call log.
          // Sent only when there genuinely is one, so every other path's request body is unchanged.
          ...(fromCallLogId ? { callLogId: fromCallLogId } : {}),
          // Session ARE (D82): the enquiry sibling of callLogId above, same reasoning as the
          // entrapment branch's own comment.
          ...(fromEnquiryId ? { enquiryId: fromEnquiryId } : {}),
        });
        const baseMessage = calloutLoggedMessage(
          logged.ref,
          logged.siteName,
          logCustomerMode === "new",
          logged.invoiced,
          logAgreed.state === "ok" && logAgreed.pence === 0,
        ) + (fromCallLogId ? " The call it came from is recorded against it." : "") + (fromEnquiryId ? " The enquiry it came from is recorded against it." : "");
        // Session AMS: the call-out above is already real by this point, so a failure here must
        // never be allowed to read as the whole submission having failed -- that would send the
        // office to retry the form and log a SECOND call-out for the same job. Caught on its own
        // and folded into the one success message, never thrown into the catch below.
        if (logEngineerId) {
          const engineerName = engineers.find((eng) => eng.id === logEngineerId)?.name ?? "the selected engineer";
          try {
            await api.callouts.assign(logged.id, logEngineerId);
            setLogResult(`${baseMessage} It has been dispatched to ${engineerName} and now appears on their phone.`);
          } catch (err) {
            setLogResult(
              `${baseMessage} It could not be assigned to ${engineerName}: ${friendlyApiError(err, "please try again.")} Assign it from the list below instead.`,
            );
          }
        } else {
          setLogResult(baseMessage);
        }
      }
      // Everything committed: clear the chain state so the next New customer starts clean and can
      // never resume onto the customer just finished.
      chain.reset();
      setNewForm({ ...emptyNewCustomer });
      // Session ANV: the call has now been turned into work, so the link is spent. Clearing it is
      // what stops a second call-out logged straight afterwards, from the same page visit, being
      // silently recorded against the same call.
      setFromCallLogId(null);
      setMode(null);
      await reloadCallouts();
    } catch (err) {
      if (err instanceof ApiError && err.details) setFieldErrors(err.details);
      else setFormError(friendlyApiError(err, logIsEntrapment ? "Could not log the entrapment. Please try again." : "Could not log the call-out. Please try again."));
    } finally {
      setSaving(false);
    }
  }

  // The submit button opens the branded confirm (Session AKA) instead of raising the call-out
  // directly; the actual raise happens from doSubmitEmergency once the office has answered it.
  function openEmergencyConfirm(e: FormEvent) {
    e.preventDefault();
    if (!canEmergency) return;
    setEmergencyConfirmOpen(true);
    // Session ASW (D22): check the caller against the existing customers before anything is logged. The server runs
    // the same check again on the create, so this is what the office sees, not what decides.
    setApplyMatchId(null);
    setCallerCheck("loading");
    // Session ASW (review L1): only the latest check may land.
    const seq = ++callerCheckSeq.current;
    Promise.resolve()
      .then(() =>
        api.callouts.emergencyCallerMatch({
          customerName: emForm.customerName.trim(),
          customerEmail: emForm.customerEmail.trim(),
          customerPhone: emForm.customerPhone.trim() || undefined,
          siteAddress: emForm.siteAddress.trim(),
        }),
      )
      .then((r) => {
        if (seq === callerCheckSeq.current) setCallerCheck(r);
      })
      .catch(() => {
        if (seq === callerCheckSeq.current) setCallerCheck("error");
      });
  }

  // notifyNow: true when the office pressed "Log and notify now" on the confirm -- the caller's
  // confirmation email (and text, if they gave a mobile) is sent for real instead of the usual
  // Gmail-draft-for-review AJI leaves as the default.
  async function doSubmitEmergency(notifyNow: boolean) {
    setEmergencyConfirmOpen(false);
    setSaving(true);
    setFieldErrors({});
    setFormError(null);
    try {
      const result = await api.callouts.emergency({
        customerName: emForm.customerName.trim(),
        customerEmail: emForm.customerEmail.trim(),
        customerPhone: emForm.customerPhone.trim() || undefined,
        siteAddress: emForm.siteAddress.trim(),
        faultDescription: emForm.faultDescription.trim() || undefined,
        reportedAt: new Date(emForm.reportedAt).toISOString(),
        outOfHours: emOoh,
        travelMins: emTravel,
        ...(emAgreed.state === "ok"
          ? { agreedPricePence: emAgreed.pence, ...(emAgreedReason.trim() ? { agreedPriceReason: emAgreedReason.trim() } : {}) }
          : {}),
        ...(notifyNow ? { notifyNow: true } : {}),
        ...(applyMatchId ? { matchedContractRateCustomerId: applyMatchId } : {}),
      });
      setEmergencyResult({ ...result, email: emForm.customerEmail.trim(), phone: emForm.customerPhone.trim() });
      setMode(null);
      await reloadCallouts();
    } catch (err) {
      if (err instanceof ApiError && err.details) setFieldErrors(err.details);
      else setFormError(friendlyApiError(err, "Could not raise the emergency call-out. Please try again."));
    } finally {
      setSaving(false);
    }
  }

  // In "New customer" mode the three names are what the records need; the lift's ELE number and
  // type are genuinely optional (see emptyNewCustomer). A record already committed by a failed
  // attempt no longer needs its field filled, so the check reads what exists as well as what is
  // typed, which is what lets the office retry after a partial failure.
  const canLogNew =
    (chain.created.customer || newForm.customerName.trim()) &&
    (chain.created.site || (newForm.siteName.trim() && newForm.siteAddress.trim()));
  const canLog = Boolean(
    (logCustomerMode === "new" ? canLogNew : logForm.customerId && logForm.siteId && logForm.assetId) &&
      logForm.reportedAt &&
      // Session AEW: an unparseable agreed price blocks the submit rather than being silently
      // dropped. Dropping it would log the call-out and draft the STANDARD rate while the office
      // believed it had agreed something else, which is the failure this field exists to prevent.
      logAgreed.state !== "invalid" &&
      // Session AFO: an agreed price also requires a reason.
      !logAgreedReasonMissing,
  );
  // Best-effort: when the site address is entered, pre-fill the travel dropdown with the
  // drive time from the ELE base (Google Maps). Only fills when travel is still 0, so a
  // manual pick is never clobbered; silent on any failure (Maps may be unset).
  async function prefillTravelFromAddress() {
    const address = emForm.siteAddress.trim();
    if (!address || emTravel !== 0) return;
    try {
      const { travelMins } = await api.callouts.travelEstimate(address);
      if (travelMins != null && travelMins > 0) setEmTravel(travelMins);
    } catch {
      /* best-effort: leave the travel field as the office set it */
    }
  }

  // The same best-effort drive-time pre-fill for the new-customer site address (Session AEP
  // adversarial review). Only fills when travel is still 0, so a manual pick is never clobbered;
  // silent on any failure, exactly as the emergency form's version is.
  async function prefillTravelFromNewSiteAddress() {
    const address = newForm.siteAddress.trim();
    if (!address || logTravel !== 0) return;
    try {
      const { travelMins } = await api.callouts.travelEstimate(address);
      if (travelMins != null && travelMins > 0) setLogTravel(travelMins);
    } catch {
      /* best-effort: leave the travel field as the office set it */
    }
  }

  // Session ASW (D22, review M1): what the caller is quoted on the confirm. An agreed price wins, as on the server; else
  // the Core in-contract card when the office ticked a matched customer, else the non-contract card the form shows.
  const emergencyConfirmPriceLine = (() => {
    if (emAgreed.state === "ok") return `Agreed price: ${gbp(emAgreed.pence / 100)} plus VAT.`;
    const est = applyMatchId
      ? estimateCallout({ contract: true, tier: "core", outOfHours: emOoh, travelMins: emTravel })
      : estimateCallout({ contract: false, outOfHours: emOoh, travelMins: emTravel });
    return applyMatchId
      ? `Estimate at the in-contract Core rate: ${gbp(est.totalNet)} plus VAT (${gbp(est.totalGross)} inc VAT).`
      : `Estimate at the non-contract rate: ${gbp(est.totalNet)} plus VAT (${gbp(est.totalGross)} inc VAT).`;
  })();

  const canEmergency = Boolean(
    emForm.customerName.trim() &&
      emForm.customerEmail.trim() &&
      emForm.siteAddress.trim() &&
      emForm.reportedAt &&
      // Session AEW: as canLog. On this path the figure also becomes a Stripe charge, so an
      // unparseable one is blocked here rather than quietly falling back to the card rate.
      emAgreed.state !== "invalid" &&
      // Session AFO: an agreed price also requires a reason.
      !emAgreedReasonMissing,
  );

  // Session AMT: sort/filter over the already-loaded list (api.callouts.list() returns every
  // call-out unpaginated -- confirmed before building this). Parsed straight from the URL query
  // string so a reload or a shared link reproduces the same view; the pure logic (parsing, the AND
  // predicate, the sort accessors) lives in calloutListFilters.ts and is unit tested there.
  const visibleCallouts = callouts;

  // Clicking a sortable column header: the shared three-step asc/desc/default cycle every other
  // office list table uses (Session AU), persisted here in the URL rather than sessionStorage.
  const toggleSort = useCallback(
    (key: string) => {
      const next = nextSortState(sortState, key);
      const params = new URLSearchParams(searchParams);
      if (next.sortKey) {
        params.set("sort", next.sortKey);
        params.set("dir", next.sortDir);
      } else {
        params.delete("sort");
        params.delete("dir");
      }
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams, sortState],
  );

  // Session ALY: the row's action controls (lifecycle primary + the report/PDF/OverflowMenu
  // grammar Session AEW built), extracted so the desktop table cell and the mobile card's
  // actions slot render the exact same JSX rather than two hand-maintained copies that could
  // drift apart -- every action available at a desk stays available on a phone.
  function renderCalloutActions(c: CalloutListItem) {
    const cancelled = c.status === "cancelled";
    const cancellable = !cancelled && c.status !== "complete";
    const actions = calloutRowActions({
      isAdmin,
      canDispatch,
      cancelled,
      cancellable,
      hasEngineer: Boolean(c.engineerName),
      hasJobSheet: Boolean(c.jobSheetJobId),
    });
    const inMenu = (a: Parameters<typeof actions.menu.includes>[0]) => actions.menu.includes(a);
    return (
      <>
        {actions.primary === "assign" && (
          <button
            type="button"
            onClick={() => openAssign(c)}
            // max-sm: the ACW 44px tap-target floor, which ConfirmButton and the
            // OverflowMenu trigger beside it already carry. Measured at 26px here
            // before this session, when it was one of six crowded controls;
            // it is now a primary and has to be reachable with a thumb.
            className="rounded-ele border border-ele-blue px-2.5 py-1 text-xs text-ele-blue transition hover:bg-ele-blue hover:text-ele-bg max-sm:inline-flex max-sm:min-h-11 max-sm:items-center"
          >
            Assign
          </button>
        )}
        {actions.primary === "complete" && (
          <ConfirmButton
            onConfirm={() => completeCallout(c.id)}
            label="Complete"
            confirmLabel={c.isEmergency || c.isEntrapment ? "Confirm? Sends survey" : "Confirm complete?"}
            busy={completingId === c.id}
            busyLabel="Completing..."
            title={
              c.isEmergency || c.isEntrapment
                ? "Marks this emergency call-out complete. The caller receives a satisfaction survey 24 hours later."
                : "Marks this call-out complete."
            }
            className={buttonClasses("secondary", "sm")}
          />
        )}
        {actions.primary === null && c.reportRef && (
          <span className="inline-flex flex-wrap items-center gap-2">
            {c.reportSentAt ? (
              <StatusPill
                word={`Released ${formatDate(c.reportSentAt)}`}
                tone={STATUS_REGISTRY.releaseGate.sent.tone}
                icon={STATUS_REGISTRY.releaseGate.sent.icon}
                title={`Released to ${c.reportSentTo ?? "the customer"} on ${formatDate(c.reportSentAt)}`}
              />
            ) : (
              c.hasReportPdf && (
                <>
                  <StatusPill word="Not yet released" tone={STATUS_REGISTRY.releaseGate.awaiting_release.tone} icon={STATUS_REGISTRY.releaseGate.awaiting_release.icon} />
                  <ConfirmButton
                    onConfirm={() => releaseReport(c.id)}
                    label="Release"
                    confirmLabel="Confirm? Drafts the customer email"
                    busy={sendingReportId === c.id}
                    busyLabel="Releasing..."
                    title="Opens this report on the customer portal and drafts the completion email (and, where eligible, the survey) to Gmail for the office to review and send."
                    className={buttonClasses("secondary", "sm")}
                  />
                </>
              )
            )}
            {c.hasReportPdf && (
              <button
                type="button"
                onClick={() => window.open(api.callouts.reportPdfHref(c.id), "_blank", "noopener,noreferrer")}
                title="Open the filed attendance report PDF"
                className="rounded-ele border border-ele-blue px-2.5 py-1 text-xs text-ele-blue transition hover:bg-ele-blue hover:text-ele-bg"
              >
                View PDF
              </button>
            )}
          </span>
        )}
        <OverflowMenu label={`Actions for ${c.ref}`}>
            {/* Session AQW (W-6): this call-out's own record page (SLA clock, originating call,
                sheet with approval inline, charge, invoice, photos, follow-up tech visit, audit
                timeline) -- additive; every control below is unchanged and still one click away. */}
            <Link to={`/office/callouts/${encodeURIComponent(c.id)}`} className={overflowItemCls}>
              Open record
            </Link>
            {inMenu("reassign") && (
              <button type="button" onClick={() => openAssign(c)} className={overflowItemCls}>
                Reassign
              </button>
            )}
            {inMenu("complete") && (
              <ConfirmButton
                onConfirm={() => completeCallout(c.id)}
                label="Complete"
                confirmLabel={c.isEmergency || c.isEntrapment ? "Confirm? Sends survey" : "Confirm complete?"}
                busy={completingId === c.id}
                busyLabel="Completing..."
                // Session AEW review fix: the same warning the primary
                // carries. An emergency completion sends the caller a
                // survey, and that must not stop being said just because
                // the control moved into the menu.
                title={
                  c.isEmergency || c.isEntrapment
                    ? "Marks this emergency call-out complete. The caller receives a satisfaction survey 24 hours later."
                    : "Marks this call-out complete."
                }
                className={overflowItemCls}
              />
            )}
            {inMenu("charge") && (
              <button
                type="button"
                onClick={() => setChargeTarget(c)}
                className={overflowItemCls}
                title="Compute the labour and travel charge"
              >
                Charge
              </button>
            )}
            {inMenu("photos") && (
              <button
                type="button"
                onClick={() => setPhotoTarget(c)}
                className={overflowItemCls}
                title="View the photos taken on this call-out"
              >
                Photos
              </button>
            )}
            {/* Session AEY: generate the Call-Out Attendance Report for this
                call-out (pre-filled from the record; PDF + Xero draft). */}
            <button
              type="button"
              onClick={() => openReport(c)}
              disabled={reportLoadingId === c.id}
              className={overflowItemCls}
              title="Generate the call-out attendance report (PDF + Xero draft)"
            >
              {reportLoadingId === c.id ? "Opening..." : c.status === "complete" ? "Report" : "Generate report"}
            </button>
            {c.hasReportPdf && (
              <button
                type="button"
                onClick={() => window.open(api.callouts.reportPdfHref(c.id), "_blank", "noopener,noreferrer")}
                className={overflowItemCls}
                title="Open the filed attendance report PDF"
              >
                Open PDF
              </button>
            )}
            {c.reportRef && !c.hasReportPdf && (
              <button
                type="button"
                onClick={() => openReport(c)}
                disabled={reportLoadingId === c.id}
                className={overflowItemCls}
                title="No PDF is filed for this report. Generate it again, with Save to Drive on."
              >
                PDF not filed: regenerate
              </button>
            )}
            {c.reportRef && (
              <button
                type="button"
                onClick={() => setRefTarget(c)}
                className={overflowItemCls}
                title={`Correct the reference on the attendance report (currently ${c.reportRef})`}
              >
                Correct reference
              </button>
            )}
            {inMenu("cancel") && (
              <button type="button" onClick={() => setCancellingCallout(c)} className={overflowItemCls} title="Cancel this call-out.">
                Cancel
              </button>
            )}
            {inMenu("delete") && (
              <button
                type="button"
                onClick={() => {
                  setDeleteError(null);
                  setDeleteTarget({ id: c.id, ref: c.ref });
                }}
                className={overflowDangerItemCls}
              >
                Delete
              </button>
            )}
        </OverflowMenu>
      </>
    );
  }

  return (
    <div>
      <PageHeader
        className="mb-4"
        title="Call-outs"
        subtitle="Reactive and emergency visits logged against your lifts."
        actions={
          mode === null ? (
            // Session AGY mobile layout: below sm the row becomes a two-column grid -- Emergency
            // spans the full width on top, the two workaday actions sit side by side under it, all
            // full-width tap targets (no more centred wrap). From sm up the desktop flex row is
            // unchanged.
            <div className="flex flex-wrap items-center gap-2 max-sm:grid max-sm:w-full max-sm:grid-cols-2">
              {/* The icons hide below sm (review fix, MEASURED): in AGY's two-column grid the
                  report button's label + icon exceeds its 167.5px cell, wrapping the label to
                  two lines; hiding all three icons at max-sm restores the verified geometry. */}
              {/* SESSION SC7B (the calm screens, part 2): Log Call-out is the one primary action and Emergency Call-out, the
                  life-safety path, stays visible and never moves into a menu. New call-out report (a report for a visit
                  already made) is the one secondary action, in the shared overflow menu. */}
              <Button
                variant="destructive"
                onClick={openEmergency}
                className="max-sm:col-span-2"
                icon={<AlertTriangle aria-hidden size={15} className="max-sm:hidden" />}
              >
                Emergency Call-out
              </Button>
              <Button onClick={() => openLog()} icon={<Phone aria-hidden size={15} className="max-sm:hidden" />}>
                Log Call-out
              </Button>
              {/* Session EXP1: Export CSV joins the one menu that is already here (the list as it is showing: statuses, site, dates, search and sort). */}
              <ExportCsvMenu
                list="callouts"
                label="More call-out actions"
                disabled={pageQuery.empty}
                params={(({ empty: _empty, ...asked }) => asked)(pageQuery)}
                extraItems={
                  <button type="button" role="menuitem" onClick={openNewReport} className={overflowItemCls}>
                    New call-out report
                  </button>
                }
              />
            </div>
          ) : undefined
        }
      />

      {deleteResult && (
        <div role="status" className="mb-6 flex items-start justify-between gap-3 rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
          <p className="text-sm text-ele-body">{deleteResult}</p>
          <button type="button" onClick={() => setDeleteResult(null)} className="text-xs text-ele-body underline hover:text-ele-strong">
            Dismiss
          </button>
        </div>
      )}

      {emergencyResult && (
        <div role="status" className="mb-6 rounded-ele border border-ele-green bg-ele-surface p-5">
          <h2 className="font-heading text-lg text-ele-green">Emergency call-out raised: {emergencyResult.jobRef}</h2>
          <p className="mt-1 text-sm text-ele-body">
            {emergencyResult.estimateNet <= 0
              ? "Agreed free of charge. There is nothing for the caller to pay."
              : `Total: ${gbp(emergencyResult.estimateNet)} plus VAT (${gbp(emergencyResult.estimateGross)} inc VAT). Payment is upfront or in cash on arrival before works commence.`}
          </p>
          <p className="mt-1 text-sm text-ele-body">
            {emergencyResult.invoiced
              ? "A draft invoice was raised in Xero (awaiting review)."
              : emergencyResult.estimateNet <= 0
                ? // Session AEW review fix: at an agreed price of zero the platform deliberately
                  // raises nothing. Blaming Xero for that sends the office looking for a
                  // connection fault that does not exist.
                  "It was agreed free of charge, so no invoice was raised."
                : "No draft invoice was raised (Xero not connected)."}
            {" "}
            {/* Session AJI: the confirmation is a Gmail draft BY DEFAULT, so this line tells the
                office what it must DO rather than claiming a delivery that has not happened, unless
                the office confirmed an immediate send (Session AKA), in which case it truthfully
                says the caller has already been told. */}
            {emergencyResult.sent
              ? `A confirmation email has been sent to ${emergencyResult.email} with their job reference and the Pay now link${emergencyResult.phone ? ", and a text has gone to their mobile" : ""}.`
              : emergencyResult.drafted
                ? `A confirmation email to ${emergencyResult.email} is waiting in Gmail Drafts. Review it and press send: the caller has not been contacted yet, by email or by text.`
                : "No confirmation email could be prepared, and no text was sent. Contact the caller directly."}
          </p>
          {emergencyResult.paymentUrl && (
            <p className="mt-1 break-all text-sm text-ele-body">
              Payment link:{" "}
              <a href={emergencyResult.paymentUrl} target="_blank" rel="noopener noreferrer" className="text-ele-green hover:underline">
                {emergencyResult.paymentUrl}
              </a>
            </p>
          )}
        </div>
      )}

      {/* An unfinished chain, while the form is CLOSED. Cancel keeps the created records (they are
          real and on the registers), so closing the form must not make them invisible: without
          this the office has no way to know anything is outstanding, and reopening would look
          like a fresh start. Reopening resumes. */}
      {mode === null && chain.created.customer && (
        <div role="status" className="mb-6 rounded-ele border border-ele-danger bg-ele-surface p-4 text-sm">
          <p className="font-heading tracking-wide text-ele-danger">A new customer is not finished</p>
          <p className="mt-1 text-ele-body">
            {chain.created.site ? "A customer and site were created" : "A customer was created"} but the call-out was not logged. Open Log Call-out to carry on, or leave it: the records are on the registers either way.
          </p>
          <ul className="mt-2 space-y-1">
            <li>
              Customer:{" "}
              <Link to={`/office/customers/${chain.created.customer.id}`} className="text-ele-green hover:underline">
                {chain.created.customer.name}
              </Link>
            </li>
            {chain.created.site && (
              <li>
                Site:{" "}
                <Link to={`/office/sites/${chain.created.site.id}`} className="text-ele-green hover:underline">
                  {chain.created.site.name}
                </Link>
              </li>
            )}
          </ul>
          <button
            type="button"
            onClick={() => {
              chain.reset();
              setNewForm({ ...emptyNewCustomer });
            }}
            className="mt-3 text-xs text-ele-body underline hover:text-ele-strong"
          >
            Leave it there and start fresh next time
          </button>
        </div>
      )}

      {logResult && (
        <div role="status" className="mb-6 flex items-start justify-between gap-3 rounded-ele border border-ele-green bg-ele-surface p-4">
          <p className="text-sm text-ele-body">{logResult}</p>
          <button type="button" onClick={() => setLogResult(null)} className="text-xs text-ele-body underline hover:text-ele-strong">
            Dismiss
          </button>
        </div>
      )}

      {mode === "log" && (
        <form onSubmit={submitLog} className={`mb-6 rounded-ele border bg-ele-surface p-5 ${logIsEntrapment ? "border-ele-danger" : "border-ele-purple-accent"}`}>
          <h2 className={`mb-3 font-heading text-xl ${logIsEntrapment ? "text-ele-danger" : "text-ele-blue"}`}>
            {logIsEntrapment ? "Log entrapment (person trapped)" : logCustomerMode === "new" ? "Log call-out (new customer)" : "Log call-out (existing customer)"}
          </h2>

          {/* Session ANV: said on screen rather than left implicit. The office arrived here from a
              logged call, and the only visible sign of it would otherwise be a customer that
              happened to be preselected -- which is indistinguishable from having picked it by hand.
              The next two fields are the Director's own steps 2 and 3, so the copy names them. */}
          {(fromCallLogId || fromEnquiryId) && (
            <p className="mb-4 rounded-ele border border-ele-green bg-ele-inset px-3 py-2 text-sm text-ele-strong">
              Raising this from a logged {fromEnquiryId ? "enquiry" : "call"}. Choose the site, then the lift on that site, then describe the fault. The{" "}
              {fromEnquiryId ? "enquiry" : "call"} will be recorded against the call-out, so the completed job can be traced back to it.
            </p>
          )}

          {/* Existing customer, or create the records now (Session AEP). The Emergency panel is
              still the right tool for a genuinely one-off caller: this is the lasting-record path. */}
          <div className="mb-4">
            <span className="mb-1.5 block text-sm">Who is this for?</span>
            <div className="flex flex-wrap gap-2">
              {(
                [
                  { value: "existing", label: "Existing customer" },
                  { value: "new", label: "New customer" },
                ] as const
              ).map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  aria-pressed={logCustomerMode === opt.value}
                  onClick={() => {
                    if (logCustomerMode === opt.value) return;
                    setLogCustomerMode(opt.value);
                    setFieldErrors({});
                    setFormError(null);
                  }}
                  // A record already created cannot be abandoned by flipping back to the picker:
                  // the office finishes (or leaves) the chain it started, so nothing is stranded.
                  disabled={Boolean(chain.created.customer) && opt.value === "existing"}
                  className={`max-sm:min-h-11 rounded-ele border px-4 py-1.5 font-menu text-xs tracking-wide transition disabled:opacity-40 ${
                    logCustomerMode === opt.value
                      ? "border-ele-green bg-ele-inset text-ele-green"
                      : "border-ele-purple-accent text-ele-body hover:text-ele-green"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {logCustomerMode === "new" && (
              <p className="mt-2 text-xs text-ele-body">
                This creates a new customer record with no maintenance contract. You can add a contract for them later from their customer page.
              </p>
            )}
          </div>
          {/* Entrapment is a TYPE selected inside the call-out flow (Session ACY): tick it to
              dispatch immediately on the 60-minute SLA. A full 4-side border, not a banned left
              accent stripe. */}
          <label
            htmlFor="logEntrapment"
            className={`mb-4 flex items-start gap-3 rounded-ele border px-3 py-2.5 ${logIsEntrapment ? "border-ele-danger bg-ele-danger/10" : "border-ele-purple-accent bg-ele-surface"}`}
          >
            <input
              id="logEntrapment"
              type="checkbox"
              checked={logIsEntrapment}
              onChange={(e) => setLogIsEntrapment(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-ele-danger"
            />
            <span className="text-sm">
              <span className={`font-heading tracking-wide ${logIsEntrapment ? "text-ele-danger" : "text-ele-strong"}`}>Someone is trapped in the lift (entrapment)</span>
              <span className="block text-xs text-ele-body">The highest priority. Alerts the office and active engineers immediately and starts a 60-minute response clock. No charge is estimated now.</span>
            </span>
          </label>
          <div className="grid gap-4 sm:grid-cols-2">
            {logCustomerMode === "existing" ? (
              <>
                <div>
                  <RecordPicker
                    kind="customer"
                    id="customerId"
                    label="Customer"
                    required
                    value={logForm.customerId}
                    onChange={(id) => onCustomer(id)}
                    filters={{ customerType: "all" }}
                    placeholder="Search for a customer"
                  />
                  {/* Session SC9B: a flag, never a block. One amber line for an account on stop; the person carries on with the same press. */}
                  <AccountOnStopLine customerId={logForm.customerId} urgent={logIsEntrapment} />
                </div>

                <div>
                  <RecordPicker
                    kind="site"
                    id="siteId"
                    label="Site"
                    required
                    value={logForm.siteId}
                    onChange={(id) => onSite(id)}
                    filters={{ customerId: logForm.customerId || undefined }}
                    disabled={!logForm.customerId}
                    placeholder={logForm.customerId ? "Search this customer's sites" : "Choose a customer first"}
                  />
                </div>

                <div>
                  <RecordPicker
                    kind="lift"
                    id="assetId"
                    label="Lift"
                    required
                    value={logForm.assetId}
                    onChange={(id, rec) => {
                      setLogForm((f) => ({ ...f, assetId: id }));
                      const covered = rec?.raw.hasContract;
                      setPickedLiftCovered(id ? { assetId: id, covered: typeof covered === "boolean" ? covered : null } : null);
                    }}
                    filters={{ siteId: logForm.siteId || undefined, includeRetired: false }}
                    disabled={!logForm.siteId}
                    placeholder={logForm.siteId ? "Search this site's lifts" : "Choose a site first"}
                  />
                  {fieldErrors.assetId?.map((msg) => (
                    <p key={msg} role="alert" className="mt-1 text-xs text-ele-strong">{msg}</p>
                  ))}
                </div>
              </>
            ) : (
              <>
                {/* autoComplete off: these capture the CALLER's details, so the operator's own
                    saved name/email must never be autofilled into them (as the Emergency form). */}
                <div>
                  <label className="mb-1 block text-sm" htmlFor="ncName">Customer name<span className="text-ele-green"> *</span></label>
                  <input
                    id="ncName"
                    autoComplete="off"
                    value={chain.created.customer ? chain.created.customer.name : newForm.customerName}
                    disabled={Boolean(chain.created.customer)}
                    onChange={(e) => setNewForm((f) => ({ ...f, customerName: e.target.value }))}
                    className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green disabled:opacity-60"
                  />
                  {fieldErrors.name?.map((m) => <p key={m} role="alert" className="mt-1 text-xs text-ele-strong">{m}</p>)}
                </div>

                <div>
                  <label className="mb-1 block text-sm" htmlFor="ncEmail">Email address</label>
                  <input
                    id="ncEmail"
                    type="email"
                    autoComplete="off"
                    value={newForm.customerEmail}
                    disabled={Boolean(chain.created.customer)}
                    onChange={(e) => setNewForm((f) => ({ ...f, customerEmail: e.target.value }))}
                    className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green disabled:opacity-60"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm" htmlFor="ncPhone">Phone number</label>
                  <input
                    id="ncPhone"
                    autoComplete="off"
                    value={newForm.customerPhone}
                    disabled={Boolean(chain.created.customer)}
                    onChange={(e) => setNewForm((f) => ({ ...f, customerPhone: e.target.value }))}
                    className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green disabled:opacity-60"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm" htmlFor="ncSiteName">Site name<span className="text-ele-green"> *</span></label>
                  <input
                    id="ncSiteName"
                    autoComplete="off"
                    value={chain.created.site ? chain.created.site.name : newForm.siteName}
                    disabled={Boolean(chain.created.site)}
                    onChange={(e) => setNewForm((f) => ({ ...f, siteName: e.target.value }))}
                    placeholder="e.g. Rosina Court"
                    className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green disabled:opacity-60"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="mb-1 block text-sm" htmlFor="ncSiteAddress">Site address<span className="text-ele-green"> *</span></label>
                  <input
                    id="ncSiteAddress"
                    autoComplete="off"
                    value={newForm.siteAddress}
                    disabled={Boolean(chain.created.site)}
                    onChange={(e) => setNewForm((f) => ({ ...f, siteAddress: e.target.value }))}
                    // Pre-fill travel from the drive time, exactly as the Emergency form does. At the
                    // non-contract GBP 120/hr this is the largest line the office is likeliest to
                    // forget, on the one customer type with no contract absorbing it.
                    onBlur={prefillTravelFromNewSiteAddress}
                    className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green disabled:opacity-60"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm" htmlFor="ncEle">Lift ID (ELE number)</label>
                  <input
                    id="ncEle"
                    autoComplete="off"
                    value={newForm.eleNumber}
                    disabled={Boolean(chain.created.asset)}
                    onChange={(e) => setNewForm((f) => ({ ...f, eleNumber: e.target.value }))}
                    className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green disabled:opacity-60"
                  />
                  <p className="mt-1 text-xs text-ele-body">Leave blank if they do not have one. ELE numbers are allocated at signing.</p>
                </div>

                <div>
                  <label className="mb-1 block text-sm" htmlFor="ncLiftType">Lift type</label>
                  <select
                    id="ncLiftType"
                    value={newForm.liftType}
                    disabled={Boolean(chain.created.asset)}
                    onChange={(e) => setNewForm((f) => ({ ...f, liftType: e.target.value }))}
                    className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green disabled:opacity-60"
                  >
                    {NEW_LIFT_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
              </>
            )}

            {logIsEntrapment ? (
              <div>
                <label className="mb-1 block text-sm" htmlFor="entCaller">Caller name (optional)</label>
                <input
                  id="entCaller"
                  value={entCaller}
                  onChange={(e) => setEntCaller(e.target.value)}
                  className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green"
                />
              </div>
            ) : (
              <div>
                <label className="mb-1 block text-sm" htmlFor="reportedAt">Date and time raised<span className="text-ele-green"> *</span></label>
                <input
                  id="reportedAt"
                  // Session ADQ: date AND time, defaulting to now. This was a date-only field, which
                  // parses to UTC midnight, so a response time measured from it would have been
                  // fiction and every ordinary call-out would have been recorded UNMEASURED. The
                  // time of day is what makes the response clock honest, so it is captured at the
                  // one moment the office actually knows it.
                  type="datetime-local"
                  value={logForm.reportedAt}
                  onChange={(e) => setLogForm((f) => ({ ...f, reportedAt: e.target.value }))}
                  className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green"
                />
                <p className="mt-1 text-xs text-ele-body">When the fault was reported to us. The response clock starts here.</p>
                {fieldErrors.reportedAt?.map((msg) => (
                  <p key={msg} role="alert" className="mt-1 text-xs text-ele-strong">{msg}</p>
                ))}
              </div>
            )}

            <div className="sm:col-span-2">
              <label className="mb-1 block text-sm" htmlFor="logFault">{logIsEntrapment ? "Description" : "Fault reported"}</label>
              <textarea
                id="logFault"
                rows={3}
                value={logForm.faultDescription}
                onChange={(e) => setLogForm((f) => ({ ...f, faultDescription: e.target.value }))}
                placeholder={logIsEntrapment ? "e.g. Person trapped between floors, doors will not open. Leave blank to log as 'Person trapped in lift'." : "What was reported? e.g. lift stuck between floors, doors not closing."}
                className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green"
              />
            </div>

            {/* The rate/tier/estimate only apply to a routine call-out; an entrapment is dispatched
                immediately and any charge is computed later from the visit. */}
            {!logIsEntrapment && logCustomerMode === "new" && (
              <>
                <RateControls idPrefix="log" ooh={logOoh} onOoh={setLogOoh} travel={logTravel} onTravel={setLogTravel} />
                <AgreedPriceFields
                  idPrefix="log"
                  price={logAgreedPrice}
                  onPrice={setLogAgreedPrice}
                  reason={logAgreedReason}
                  onReason={setLogAgreedReason}
                  parsed={logAgreed}
                />

                {/* A new customer has no maintenance contract, so there is no tier to pick and the
                    published non-contract rate applies. This is the SAME calculation the Emergency
                    intake shows, and the server rates the DRAFT on the same basis from the lift's
                    own (absent) contract, so the estimate and the invoice cannot disagree. */}
                <CalloutCostPanel
                  est={estimateCallout({ contract: false, outOfHours: logOoh, travelMins: logTravel })}
                  agreed={logAgreed}
                  outOfHours={logOoh}
                  travelMins={logTravel}
                  accent="purple"
                  footnote="Non-contract rate: a new customer has no maintenance contract."
                  extrasNote="The office adds any additional hours."
                />
              </>
            )}

            {!logIsEntrapment && logCustomerMode === "existing" && (
              <>
                <RateControls idPrefix="log" ooh={logOoh} onOoh={setLogOoh} travel={logTravel} onTravel={setLogTravel} />

                {/* Session BAF (C2-017): this lift's contract was terminated for cause. Internal; the office decides whether to attend. */}
                {logForm.assetId && liftBasis?.assetId === logForm.assetId && liftBasis.terminatedFor ? (
                  <p role="status" className="mb-2 text-xs font-menu uppercase tracking-wide text-[var(--ele-amber-text)]">
                    {liftBasis.terminatedFor}
                  </p>
                ) : null}

                {/* The tier only means anything for a lift that is actually on a contract. */}
                {liftInContract !== false && (
                  <div>
                    <label className="mb-1 block text-sm" htmlFor="logTier">Contract tier (for the estimate)</label>
                    <select
                      id="logTier"
                      value={logTier}
                      onChange={(e) => setLogTier(e.target.value as CalloutTier)}
                      className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green"
                    >
                      {LOG_TIERS.map(({ tier, label }) => (
                        <option key={tier} value={tier}>{label}</option>
                      ))}
                    </select>
                  </div>
                )}

                <AgreedPriceFields
                  idPrefix="log"
                  price={logAgreedPrice}
                  onPrice={setLogAgreedPrice}
                  reason={logAgreedReason}
                  onReason={setLogAgreedReason}
                  parsed={logAgreed}
                />

                {(() => {
                  // The estimate follows the LIFT's own coverage, not an assumption. The server
                  // rates the DRAFT from whether that lift is on an active contract, so a lift
                  // with none is billed at the published non-contract rate; showing the Core rate
                  // here would put a figure on screen that the invoice then contradicts, on
                  // exactly the customers where the difference is doubled.
                  const covered = liftInContract !== false;
                  const est = covered
                    ? estimateCallout({ contract: true, tier: logTier, outOfHours: logOoh, travelMins: logTravel })
                    : estimateCallout({ contract: false, outOfHours: logOoh, travelMins: logTravel });
                  return (
                    <CalloutCostPanel
                      est={est}
                      agreed={logAgreed}
                      outOfHours={logOoh}
                      travelMins={logTravel}
                      accent="purple"
                      footnote={
                        covered
                          ? "Estimate for the selected tier; the invoice uses the contract's actual tier."
                          : "Non-contract rate: this lift is not on a maintenance contract."
                      }
                      extrasNote="The office adds any additional hours."
                    />
                  );
                })()}
              </>
            )}

            {/* Session AMS: optional engineer assignment straight from this form. Session ASM (the D57 rollout gap):
                admin AND office, the same canDispatch gate as the row-level Assign/Reassign control
                (calloutRowActions.ts) and the server's own PATCH /api/callouts/:id/assign guard. */}
            {!logIsEntrapment && canDispatch && (
              <div className="sm:col-span-2">
                <label className="mb-1 block text-sm" htmlFor="logEngineer">Assign engineer (optional)</label>
                <select
                  id="logEngineer"
                  value={logEngineerId}
                  onChange={(e) => setLogEngineerId(e.target.value)}
                  className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green"
                >
                  <option value="">Leave unassigned</option>
                  {engineers.map((eng) => (
                    <option key={eng.id} value={eng.id}>{eng.name}</option>
                  ))}
                </select>
                {engineerListError ? (
                  <p role="alert" className="mt-1 text-xs text-ele-danger">{engineerListError}</p>
                ) : (
                  <p className="mt-1 text-xs text-ele-body opacity-70">
                    {engineers.length === 0
                      ? "Loading engineers..."
                      : "Dispatches to their phone as soon as this call-out is logged. Leave unassigned to dispatch it later from the list below."}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Anything that went wrong AFTER a record was created. Everything that DID land is named
              with a link, so no record is ever stranded somewhere the office cannot find it, and a
              retry resumes rather than creating a second copy.
              This covers BOTH failures, deliberately: the chain stopping part way, AND the chain
              finishing but the call-out itself failing. The second is the quieter one. The records
              are just as real, and without this the office would be told only "could not log the
              call-out" while a customer, site and lift sat on the registers unmentioned. */}
          {/* Session ASQ, Item 3's review, F5: the new customer's email or phone belongs to a person whose data was erased.
              Pressing the ordinary button again would only be refused again; this one goes ahead, and the server audits it. */}
          {chain.chainErased && (
            <ErasedPersonNotice
              message={chain.chainErased}
              busy={saving}
              onGoAhead={() => {
                chain.allowErasedPerson();
                void runLogSubmit();
              }}
            />
          )}

          {(chain.chainFailure || (formError && chain.created.customer)) && (
            <div role="alert" className="mt-4 rounded-ele border border-ele-danger bg-ele-surface p-4 text-sm">
              <p className="font-heading tracking-wide text-ele-danger">
                {chain.chainFailure ? "Could not finish creating the records" : "The records were created, but the call-out was not logged"}
              </p>
              <p className="mt-1 text-ele-strong">{chain.chainFailure ? chain.chainFailure.message : formError}</p>
              {(chain.created.customer || chain.created.site) && (
                <>
                  <p className="mt-3 text-ele-body">Nothing was lost. This is what has been created so far:</p>
                  <ul className="mt-1 space-y-1">
                    {chain.created.customer && (
                      <li>
                        Customer:{" "}
                        <Link to={`/office/customers/${chain.created.customer.id}`} className="text-ele-green hover:underline">
                          {chain.created.customer.name}
                        </Link>
                      </li>
                    )}
                    {chain.created.site && (
                      <li>
                        Site:{" "}
                        <Link to={`/office/sites/${chain.created.site.id}`} className="text-ele-green hover:underline">
                          {chain.created.site.name}
                        </Link>
                      </li>
                    )}
                    {chain.created.asset && <li className="text-ele-body">Lift: created at the site above.</li>}
                  </ul>
                </>
              )}
              <p className="mt-3 text-ele-body">
                {chain.chainFailure
                  ? `Press the button again to carry on from the ${CHAIN_STEP_LABEL[chain.chainFailure.step]}. Anything listed above is reused, never created twice.`
                  : "Press the button again to log the call-out. The records above are reused, never created twice."}
              </p>
            </div>
          )}

          {/* The plain error line, for every case the panel above does not already explain. */}
          {formError && !(logCustomerMode === "new" && chain.created.customer) && (
            <p role="alert" className="mt-4 rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-sm text-ele-strong">{formError}</p>
          )}

          <div className="mt-5 flex flex-wrap items-center gap-3">
            {logCustomerMode === "new" ? (
              // Two-step confirm naming the customer and site being created (the branded-confirm
              // house rule): this one press creates three lasting records, dispatches to an
              // engineer and raises a DRAFT invoice.
              <ConfirmButton
                onConfirm={runLogSubmit}
                disabled={!canLog}
                busy={saving}
                busyLabel={logIsEntrapment ? "Dispatching..." : "Creating and logging..."}
                label={logIsEntrapment ? "Create customer and dispatch" : "Create customer and log call-out"}
                confirmLabel={`Press again to create ${(chain.created.customer?.name || newForm.customerName).trim() || "this customer"} at ${(chain.created.site?.name || newForm.siteName).trim() || "this site"}`}
                className={buttonClasses(logIsEntrapment ? "danger" : "primary", "md")}
                // The armed state escalates, per the platform two-step standard. The default
                // armedClassName is a red TEXT colour, which is invisible on these solid fills, so
                // the whole button goes red instead: same signal, legible on both variants.
                armedClassName="!bg-ele-danger-fill !text-white ring-2 ring-ele-danger"
              />
            ) : logIsEntrapment ? (
              <Button type="submit" variant="danger" disabled={saving || !canLog}>
                {saving ? "Logging..." : "Log entrapment and dispatch"}
              </Button>
            ) : (
              <Button type="submit" disabled={saving || !canLog}>
                {saving ? "Logging..." : "Log call-out"}
              </Button>
            )}
            <button type="button" onClick={closeForm} className="rounded-ele border border-ele-purple-accent px-4 py-2">Cancel</button>
          </div>
        </form>
      )}

      {mode === "emergency" && (
        // autoComplete off: these fields capture the CALLER's details, so the
        // operator's own saved name/email must never be autofilled into them.
        <form onSubmit={openEmergencyConfirm} autoComplete="off" className="mb-6 rounded-ele border border-ele-danger bg-ele-surface p-5">
          <h2 className="mb-1 font-heading text-xl text-ele-danger">Emergency call-out (non-contract)</h2>
          {/* Session AKA: this now offers a real, immediate send from the confirm dialog raised by
              the button below, so the copy can no longer promise the draft-only path unconditionally
              (Session AJI's own correction of the pre-AJI opposite mistake). */}
          <p className="mb-4 text-xs text-ele-body opacity-80">Raises a draft invoice at the non-contract rate. The caller is checked against existing customers first: if they match a contract customer, you can choose to bill this attendance at that customer's contract rate instead. You will be asked to confirm before anything is sent to the caller: a confirmation email, with our Schedule of Fees attached, either as a Gmail draft for you to review and send, or sent immediately if you choose to notify now. Payment is upfront or in cash on arrival.</p>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm" htmlFor="emName">Customer name<span className="text-ele-green"> *</span></label>
              <input id="emName" autoComplete="off" value={emForm.customerName} onChange={(e) => setEmForm((f) => ({ ...f, customerName: e.target.value }))} className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green" />
              {fieldErrors.customerName?.map((m) => <p key={m} role="alert" className="mt-1 text-xs text-ele-strong">{m}</p>)}
            </div>
            <div>
              <label className="mb-1 block text-sm" htmlFor="emEmail">Email address<span className="text-ele-green"> *</span></label>
              <input id="emEmail" type="email" autoComplete="off" value={emForm.customerEmail} onChange={(e) => setEmForm((f) => ({ ...f, customerEmail: e.target.value }))} className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green" />
              {fieldErrors.customerEmail?.map((m) => <p key={m} role="alert" className="mt-1 text-xs text-ele-strong">{m}</p>)}
            </div>
            <div>
              <label className="mb-1 block text-sm" htmlFor="emPhone">Mobile number</label>
              <input id="emPhone" value={emForm.customerPhone} onChange={(e) => setEmForm((f) => ({ ...f, customerPhone: e.target.value }))} className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green" />
            </div>
            <div>
              <label className="mb-1 block text-sm" htmlFor="emWhen">Date and time raised<span className="text-ele-green"> *</span></label>
              <input id="emWhen" type="datetime-local" value={emForm.reportedAt} onChange={(e) => setEmForm((f) => ({ ...f, reportedAt: e.target.value }))} className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green" />
              {fieldErrors.reportedAt?.map((m) => <p key={m} role="alert" className="mt-1 text-xs text-ele-strong">{m}</p>)}
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1 block text-sm" htmlFor="emSite">Site address<span className="text-ele-green"> *</span></label>
              <input id="emSite" value={emForm.siteAddress} onChange={(e) => setEmForm((f) => ({ ...f, siteAddress: e.target.value }))} onBlur={prefillTravelFromAddress} className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green" />
              {fieldErrors.siteAddress?.map((m) => <p key={m} role="alert" className="mt-1 text-xs text-ele-strong">{m}</p>)}
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1 block text-sm" htmlFor="emFault">Fault description</label>
              <textarea id="emFault" rows={3} value={emForm.faultDescription} onChange={(e) => setEmForm((f) => ({ ...f, faultDescription: e.target.value }))} placeholder="What is the problem? e.g. passenger trapped, lift not moving." className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green" />
            </div>

            <RateControls idPrefix="em" ooh={emOoh} onOoh={setEmOoh} travel={emTravel} onTravel={setEmTravel} />
            <AgreedPriceFields
              idPrefix="em"
              price={emAgreedPrice}
              onPrice={setEmAgreedPrice}
              reason={emAgreedReason}
              onReason={setEmAgreedReason}
              parsed={emAgreed}
            />

            {/* On this path the figure is not only invoiced: it is what the Stripe payment link
                charges and what the confirmation email quotes. The server resolves it once and
                threads the same number through all three, so the caller cannot be shown one
                figure and charged another. */}
            <CalloutCostPanel
              est={estimateCallout({ contract: false, outOfHours: emOoh, travelMins: emTravel })}
              agreed={emAgreed}
              outOfHours={emOoh}
              travelMins={emTravel}
              accent="green"
              footnote="This is what the customer's payment link will charge."
              extrasNote="Additional time and parts are charged per the Schedule of Fees."
            />
          </div>

          {formError && (
            <p role="alert" className="mt-4 rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-sm text-ele-strong">{formError}</p>
          )}

          <div className="mt-5 flex gap-3">
            <Button type="submit" variant="danger" disabled={saving || !canEmergency}>
              {saving ? "Raising..." : "Raise emergency call-out"}
            </Button>
            <button type="button" onClick={closeForm} className="rounded-ele border border-ele-purple-accent px-4 py-2">Cancel</button>
          </div>
        </form>
      )}

      <EmergencyConfirmModal
        open={emergencyConfirmOpen}
        caller={emForm.customerName.trim()}
        email={emForm.customerEmail.trim()}
        phone={emForm.customerPhone.trim()}
        saving={saving}
        onCancel={() => setEmergencyConfirmOpen(false)}
        onLogOnly={() => void doSubmitEmergency(false)}
        onLogAndNotify={() => void doSubmitEmergency(true)}
        callerCheck={callerCheck}
        applyMatchId={applyMatchId}
        onApplyMatch={setApplyMatchId}
        priceLine={emergencyConfirmPriceLine}
      />

      {entResult && (
        <p role="status" className="mb-4 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">{entResult}</p>
      )}

      {loading ? (
        <Loader />
      ) : error ? (
        <p role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-strong">
          Could not load the call-outs. Please try again in a moment.
        </p>
      ) : (
        <>
          {cancelError && (
            <p role="alert" className="mb-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
              {cancelError}
            </p>
          )}
          <CalloutsFilterBar
            searchParams={searchParams}
            setSearchParams={setSearchParams}
            filterState={filterState}
          />
          {visibleCallouts.length === 0 ? (
            <EmptyState
              role={hasActiveFilters(filterState) ? "status" : undefined}
              message={
                hasActiveFilters(filterState)
                  ? "No call-outs match the current filter."
                  : filterState.allStatuses
                    ? "No call-outs logged yet."
                    : "No outstanding call-outs. Choose All statuses to see the closed ones."
              }
            />
          ) : mobileCards ? (
            /* Session ALY mobile card list. The surviving table columns below md (Site, Raised,
               Status, the always-visible Actions cell) each kept their DESKTOP percentage width
               (measured for 1280px against 8 columns), so a phone divided 375px between them
               unchanged: Site got roughly 45px and Raised roughly 38px, which is why the office
               saw "No site li...", "Equitabl..." and "41 High S...". The card stacks instead:
               the site name never truncates, and Reference/Fault/Lift/Attended -- hidden on the
               narrow table because there was nowhere to put them, not because they matter less --
               come back in the details block, in full, rather than being lost. */
            <MobileCardList label="Call-outs">
              {visibleCallouts.map((c) => {
                const cancelled = c.status === "cancelled";
                return (
                  <MobileListCard
                    key={c.id}
                    primary={
                      c.siteId ? (
                        <Link to={`/office/sites/${encodeURIComponent(c.siteId)}`} className="text-ele-green hover:underline">
                          {c.siteName}
                        </Link>
                      ) : (
                        c.siteName
                      )
                    }
                    secondary={
                      <>
                        {c.customerId ? (
                          <Link to={`/office/customers/${encodeURIComponent(c.customerId)}`} className="text-ele-green hover:underline">
                            {c.customerName}
                          </Link>
                        ) : (
                          c.customerName
                        )}
                        {c.customerId && <AccountSymbol plain customerId={c.customerId} />}
                        {c.isNonContract && <StatusPill word="Non-contract" tone="amber" className="ml-1.5" />}
                      </>
                    }
                    meta={`Raised ${formatDate(c.reportedAt)}`}
                    right={
                      <>
                        {(c.isEntrapment || c.isEmergency) && (
                          <StatusPill word={c.isEntrapment ? "Entrapment" : "Emergency"} tone="red" variant="solid" />
                        )}
                        {calloutState(c) ? (
                          <StatusPill state={calloutState(c)!} />
                        ) : (
                          <span className={`font-menu text-xs tracking-wide ${statusWordClass(c.status)} ${cancelled ? "opacity-50 line-through" : ""}`}>
                            {sentenceCaseStatus(c.status)}
                          </span>
                        )}
                        {c.engineerName && <span className="text-xs text-ele-body">{c.engineerName}</span>}
                        {c.engineerAccepted === false && <StatusPill word="Not accepted" tone="amber" />}
                      </>
                    }
                    details={
                      <div className="space-y-2 text-sm">
                        <div>
                          <span className="text-xs uppercase tracking-wide text-ele-body opacity-70">Reference</span>{" "}
                          {c.jobSheetJobId ? (
                            <Link
                              to={`/office/callouts?job=${encodeURIComponent(c.jobSheetJobId)}`}
                              className="text-ele-green underline-offset-2 hover:underline"
                            >
                              {c.reference ?? c.ref}
                            </Link>
                          ) : (
                            <span>{c.reference ?? c.ref}</span>
                          )}
                        </div>
                        {c.slaDeadline && (
                          <SlaCountdown deadline={c.slaDeadline} completedAt={c.completedAt} reportedAt={c.reportedAt} status={c.status} assignedAt={c.assignedAt ?? null} dueAt={c.slaDueAt} onHold={c.slaOnHold ?? false} heldLabel={c.slaHeldLabel ?? null} />
                        )}
                        {c.assetId && (
                          <div>
                            <span className="text-xs uppercase tracking-wide text-ele-body opacity-70">Lift</span>{" "}
                            <Link to={`/office/lifts/${encodeURIComponent(c.assetId)}`} className="ele-ref underline-offset-2 hover:underline">
                              {c.assetRef ?? "Lift"}
                            </Link>
                          </div>
                        )}
                        {c.faultDescription && (
                          <div>
                            <span className="text-xs uppercase tracking-wide text-ele-body opacity-70">Fault</span> {c.faultDescription}
                          </div>
                        )}
                        {c.attendedAt && (
                          <div>
                            <span className="text-xs uppercase tracking-wide text-ele-body opacity-70">Attended</span> {formatDate(c.attendedAt)}
                          </div>
                        )}
                      </div>
                    }
                    actions={renderCalloutActions(c)}
                  />
                );
              })}
            </MobileCardList>
          ) : (
            /* Session AEW, found by adversarial review and confirmed by measurement: this wrapper
                was `overflow-hidden`, which CLIPPED the row overflow menu. On the bottom rows the
                menu extended 126px past the wrapper and its last items (Cancel, Delete) were not
                hit-testable at all -- document.elementFromPoint at Delete's own centre returned the
                page behind it. The clip existed only to round the table's corners, and in the office
                shell the thead background is transparent (index.css:255-257) so there is nothing to
                round away. Every cell still clips its own text (.ele-list-table td is
                overflow:hidden), so the only thing this releases is the menu, which is the thing
                that has to escape.
                THE FLAG THIS COMMENT USED TO CARRY IS CLOSED (recorded here in Session AIB). It read
                "NOT FIXED HERE, and flagged: ProjectsPage.tsx:171 and ProspectsPage carry the same
                wrapper ... the proper fix is to portal the menu out of its clipping ancestor inside
                the shared component". Session AFO DID exactly that (commit f79c24c): OverflowMenu
                now createPortals its panel to document.body and positions it `fixed` from the
                trigger, so no ancestor's overflow-hidden can clip it on ANY consumer, present or
                future. Pinned by src/components/overflowMenuPortal.test.ts (source invariants, plus
                that both pages use the shared component) and OverflowMenu.test.tsx:49 (behavioural:
                the panel escapes an overflow-hidden ancestor).
                So those two pages KEEP their `overflow-hidden` wrapper deliberately: it is the
                documented house default for a list table (index.css:511-515, "the wrapper uses
                overflow-hidden (clip, never scroll)"), and stripping it wholesale would turn
                RegisterPage.test.tsx:64 red, which pins the opposite. THIS page's removal was the
                exception AEW needed before the component fix existed, not a new norm.
                Left as a live warning because a stale flag is expensive: read as open, it sent the
                Session AIA mobile audit to file the closed defect as its two highest-severity
                findings and its two headline "quick wins". */
            <div className="rounded-ele border border-ele-purple-accent">
              <table className="ele-list-table w-full text-left text-sm">
                {/* Session AEW: reallocated from MEASURED content, not guessed (the AEN lesson).
                    At 1280px the table is 974px wide. Before this the Actions column was 97.5px
                    while its buttons rendered 451px, and because .cell-actions is overflow:visible
                    and the row is justify-end, those buttons painted up to 366px LEFT of their own
                    column: straight across Status, Attended, Raised and into Fault. Widening alone
                    could never fix it (six labelled buttons plus eight data columns do not fit in
                    974px at any allocation), so the row now keeps ONE visible primary and moves the
                    rest into the shared OverflowMenu, which is the platform's own grammar for this.
                    The allocation follows one rule: a column whose values come from a BOUNDED
                    vocabulary must never clip, and a free-text column may truncate (as all three
                    always have). Bounded, with their measured needs at 1280px (content + the 24px
                    px-3 padding):
                      Reference  90 for the ELE number; the SLA line is 132.5 and VARIABLE, so that
                                 cell now WRAPS rather than being pinned to a width it can outgrow
                      Lift       90 (the .ele-ref mono ELE number)
                      Raised     96, Attended 92 (a "21 Jul 2026" date)
                      Status     102 ("in progress" is 78; the engineer name truncates)
                      Actions    145 (Complete 81 + gap + the 32px menu trigger)
                    Site, Customer and Fault are the free text, and give up the width: all three
                    truncated before this change too (Customer needed 146 against 127, Fault 431). */}
                <colgroup>
                  <col className="max-md:hidden" style={{ width: "11%" }} />
                  <col style={{ width: "12%" }} />
                  <col className="max-md:hidden" style={{ width: "10%" }} />
                  <col className="max-md:hidden" style={{ width: "12%" }} />
                  <col className="max-md:hidden" style={{ width: "9%" }} />
                  <col style={{ width: "10%" }} />
                  <col className="max-md:hidden" style={{ width: "10%" }} />
                  <col style={{ width: "11%" }} />
                  <col style={{ width: "15%" }} />
                </colgroup>
                <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
                  <tr>
                    {/* Reference (with its Entrapment/Emergency safety badges) hides below md: this
                        table has no per-row detail page (only a completed visit's Reference links
                        onward, and even then just deep-links into the records list below on the
                        same page), so mobile keeps Site + Raised + Status + the always-visible
                        Actions cell rather than a partial, sometimes-a-link identifier column.
                        Session AMT: every data column is now sortable via the shared
                        SortableHeader/useSortableColumn primitives (Session AU) -- same three-step
                        asc/desc/default cycle and nulls-last, numeric-aware compare every other
                        office list table already uses. Actions stays a plain th: it is not a data
                        column and has nothing to sort by. */}
                    <SortableHeader label="Reference" colKey="reference" sort={sortState} onToggle={toggleSort} className="max-md:hidden" />
                    <SortableHeader label="Site" colKey="site" sort={sortState} onToggle={toggleSort} />
                    <SortableHeader label="Lift" colKey="lift" sort={sortState} onToggle={toggleSort} className="max-md:hidden" />
                    <SortableHeader label="Customer" colKey="customer" sort={sortState} onToggle={toggleSort} className="max-md:hidden" />
                    <SortableHeader label="Fault" colKey="fault" sort={sortState} onToggle={toggleSort} className="max-md:hidden" />
                    <SortableHeader label="Raised" colKey="raised" sort={sortState} onToggle={toggleSort} />
                    <SortableHeader label="Attended" colKey="attended" sort={sortState} onToggle={toggleSort} className="max-md:hidden" />
                    <SortableHeader label="Status" colKey="status" sort={sortState} onToggle={toggleSort} />
                    <th className="cell-actions px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleCallouts.map((c) => {
                    const cancelled = c.status === "cancelled";
                    return (
                      <tr key={c.id} className={`border-t border-ele-purple-accent ${cancelled ? "opacity-50" : ""}`}>
                        {/* Session AEW: .cell-wrap, so the SLA line renders whole. Its text is
                            VARIABLE ("SLA exceeded by 21h 17m" measures 132.5px and a longer
                            breach measures more), and the cell was nowrap + overflow:hidden, so
                            the office was reading a clipped breach figure on exactly the rows
                            that matter most. A variable-length string must not be pinned to a
                            fixed column width; it wraps instead. */}
                        <td className={`cell-wrap max-md:hidden px-3 py-2 align-top font-heading text-ele-strong ${cancelled ? "line-through" : ""}`}>
                          {/* The ref truncates, but the Emergency flag sits on its own line so it is
                              never clipped on a narrow column (Session AJ Item 4 review). */}
                          <div className="truncate">
                            {c.jobSheetJobId ? (
                              <Link
                                to={`/office/callouts?job=${encodeURIComponent(c.jobSheetJobId)}`}
                                className="text-ele-green underline-offset-2 hover:underline"
                                title="Open the visit record for this call-out"
                              >
                                {c.reference ?? c.ref}
                              </Link>
                            ) : (
                              c.reference ?? c.ref
                            )}
                          </div>
                          {/* Safety flag + the live SLA clock. The entrapment branch stays FIRST,
                              because an entrapment is always also an emergency and must show the
                              stronger of the two flags. Since Session ADQ the clock itself is no
                              longer entrapment-only: every call-out logged with a real time of day
                              carries an attendance deadline, so a routine call-out shows the same
                              countdown. A row with no deadline is unmeasured and SlaCountdown
                              renders nothing, which is why the wrapper below keys off slaDeadline
                              rather than assuming a clock. */}
                          {(c.isEntrapment || c.isEmergency || c.slaDeadline) && (
                            <div className="mt-1 space-y-1">
                              {c.isEntrapment ? (
                                <StatusPill word="Entrapment" tone="red" variant="solid" />
                              ) : c.isEmergency ? (
                                <StatusPill word="Emergency" tone="red" variant="solid" />
                              ) : null}
                              <SlaCountdown
                                deadline={c.slaDeadline}
                                completedAt={c.completedAt}
                                reportedAt={c.reportedAt}
                                status={c.status}
                                assignedAt={c.assignedAt ?? null}
                                dueAt={c.slaDueAt}
                                onHold={c.slaOnHold ?? false}
                                heldLabel={c.slaHeldLabel ?? null}
                              />
                              {c.slaHeldLabel && !c.slaOnHold && (
                                <div className="font-menu text-[10px] tracking-wide text-ele-body" title="Time waiting on the customer, left out of the response time.">
                                  {c.slaHeldLabel}
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 align-top">
                          {c.siteId ? (
                            <Link to={`/office/sites/${encodeURIComponent(c.siteId)}`} className="text-ele-green underline-offset-2 hover:underline">
                              {c.siteName}
                            </Link>
                          ) : (
                            c.siteName
                          )}
                        </td>
                        {/* Lift column (Session ADH): the lift the call-out was logged against, linked
                            to its detail page. An emergency (non-contract) call-out has no lift, so the
                            cell is a plain dash. */}
                        <td className="max-md:hidden px-3 py-2 align-top">
                          {c.assetId ? (
                            <Link to={`/office/lifts/${encodeURIComponent(c.assetId)}`} className="ele-ref underline-offset-2 hover:underline">
                              {c.assetRef ?? "Lift"}
                            </Link>
                          ) : (
                            <span className="text-ele-body opacity-60">{EMPTY}</span>
                          )}
                        </td>
                        <td className="max-md:hidden px-3 py-2 align-top">
                          <span className="inline-flex flex-wrap items-center gap-1.5">
                            {/* SC9B: the symbol BEFORE the name, so a truncating cell cuts the name and never the symbol. */}
                            {c.customerId && <AccountSymbol customerId={c.customerId} className="!mr-0" />}
                            {c.customerId ? (
                              <Link to={`/office/customers/${encodeURIComponent(c.customerId)}`} className="text-ele-green underline-offset-2 hover:underline">
                                {c.customerName}
                              </Link>
                            ) : (
                              c.customerName
                            )}
                            {/* Session AFN: a non-contract call-out (a walk-in / one-off caller, or a
                                site with no active contract) is billed at the published card rates.
                                The badge is visibility only -- it changes nothing about the row or the
                                billing. Uses the canonical amber pill so it matches every other pill. */}
                            {c.isNonContract && <StatusPill word="Non-contract" tone="amber" />}
                          </span>
                        </td>
                        {/* Session AEW: the reported fault is free text that measures 350 to 430px,
                            so it truncated at the old width too and truncates a little harder now
                            that the bounded columns take what they measure. A title makes the whole
                            of it readable on hover, which it was not before. */}
                        <td className="max-md:hidden px-3 py-2 align-top" title={c.faultDescription ?? undefined}>
                          {c.faultDescription ?? EMPTY}
                        </td>
                        <td className="px-3 py-2 align-top">{formatDate(c.reportedAt)}</td>
                        <td className="max-md:hidden px-3 py-2 align-top">{formatDate(c.attendedAt)}</td>
                        {/* Session AEW: this carried .cell-actions, i.e. overflow:visible, which
                            is what allowed the status word, the engineer name and the neighbouring
                            action buttons to paint over one another. Status is a DATA cell: it
                            contains itself and the engineer name truncates. */}
                        <td className="px-3 py-2 align-top">
                          {/* Session AGZ sentence case: a plain data value, not a pill --
                              "In progress", never the shouted "IN PROGRESS". */}
                          {calloutState(c) ? (
                            <StatusPill state={calloutState(c)!} className="max-w-full whitespace-normal" />
                          ) : (
                            <span className={`block truncate font-menu text-xs tracking-wide ${statusWordClass(c.status)}`}>
                              {sentenceCaseStatus(c.status)}
                            </span>
                          )}
                          {c.holdReason && (c.isEntrapment || c.isEmergency) && (
                            <div className="mt-0.5 truncate text-xs text-ele-body">{holdLabel(c.holdReason, c.holdReasonText)}</div>
                          )}
                          {c.engineerName && <div className="mt-1 truncate text-xs text-ele-body">{c.engineerName}</div>}
                          {/* Session ALW: the office can see at a glance that the engineer this
                              call-out was dispatched to has not confirmed it yet. Only the pending
                              state shows: an accepted call-out is the normal case and should not
                              shout, and `engineerAccepted === false` is deliberately distinct from
                              null/undefined, which mean "nothing to say" rather than "not accepted". */}
                          {c.engineerAccepted === false && (
                            <StatusPill word="Not accepted" tone="amber" title="Assigned, but the engineer has not accepted it yet" className="mt-1" />
                          )}
                          {/* The Reference column (and its Entrapment/Emergency badge) hides below
                              md, so repeat the safety flag here on mobile only, where Status stays
                              visible; the desktop badge on Reference is unaffected. */}
                          {(c.isEntrapment || c.isEmergency) && (
                            <StatusPill word={c.isEntrapment ? "Entrapment" : "Emergency"} tone="red" variant="solid" className="mt-1 md:hidden" />
                          )}
                        </td>
                        {/* Session AEW. ONE visible primary plus the shared OverflowMenu, which is
                            the grammar that component was written for ("secondary and destructive
                            actions live here so each section keeps exactly one visible primary").
                            Every action that was here before is still here and still one click
                            away; none was removed and no guard changed.

                            The primary follows the workflow rather than being fixed: an unassigned
                            call-out needs an engineer, an assigned one needs completing. Both are
                            the same controls as before, with the same handlers, the same two-step
                            arming and the same confirm wording.

                            .cell-actions stays on this cell, and here it is load-bearing rather
                            than accidental: the menu is absolutely positioned and would be clipped
                            without overflow:visible. flex-wrap plus whitespace-normal are what make
                            overflow impossible anyway -- a control can now only ever wrap, never
                            paint outside the cell, including when a two-step button arms and its
                            label grows to "Confirm? Sends survey" (165.9px measured). */}
                        <td className="cell-actions px-3 py-2 align-top text-right">
                          <div className="flex flex-wrap items-center justify-end gap-2 whitespace-normal">{renderCalloutActions(c)}</div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {/* Session SC2C: the list is 50 at a time; the total is the server's count of every call-out matching the filters. */}
          <div className={refreshing ? "opacity-60" : undefined}>
            <Pager
              offset={offset}
              total={total}
              shown={visibleCallouts.length}
              pageSize={CALLOUT_PAGE_SIZE}
              onOffset={(o) => setPageState({ key: queryKey, offset: o })}
              noun={["call-out", "call-outs"]}
            />
          </div>
        </>
      )}

      {/* Completed call-out visit records: the call-out JOB SHEETS (the work recorded on
          site), filtered to call-out type so they live here, never on Service Records. This
          is also where a pending-approval call-out sheet is approved (the field loop). The
          Reference link above deep-links here via ?job=<jobId>. */}
      <div className="mt-10">
        <h2 className="mb-1 font-heading text-2xl tracking-wide">Completed call-out records</h2>
        <p className="mb-4 text-sm text-ele-body">The work recorded on site for each call-out visit. Select a row to see the full report and approve it.</p>
        <JobSheetList
          kind="callout"
          emptyActiveText="No call-out visit records yet."
          emptyArchivedText="No archived call-out records."
        />
      </div>

      {assignTarget && (
        <ModalShell ariaLabel="Assign an engineer" onClose={() => !assigning && setAssignTarget(null)} width="max-w-md" panelClassName="p-6">
            <h2 className="mb-2 font-heading text-xl text-ele-strong">Assign an engineer</h2>
            <p className="mb-4 text-sm text-ele-body">
              {assignTarget.ref}
              {assignTarget.siteName ? `, ${assignTarget.siteName}` : ""}. The call-out moves to in progress and appears on the engineer's app.
            </p>
            {/* Session SC5: the platform's proposed engineer, day and window, one Accept. The picker below is today's flow, unchanged. */}
            <AccountOnStopLine customerId={assignTarget.customerId} urgent={assignTarget.isEntrapment || assignTarget.isEmergency} className="mb-3 mt-0" />
            <ProposedSlotLine target={{ calloutId: assignTarget.id }} busy={assigning} onAccept={(p) => (p.engineerId ? doAssign(p.engineerId) : undefined)} />
            <label className="mb-1 block text-sm" htmlFor="assignEngineer">Engineer</label>
            <select
              id="assignEngineer"
              value={selectedEngineer}
              onChange={(e) => setSelectedEngineer(e.target.value)}
              className="mb-4 w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green"
            >
              <option value="">Select an engineer...</option>
              {engineers.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}
                </option>
              ))}
            </select>
            {engineers.length === 0 && !assignError && <p className="mb-3 text-sm text-ele-body">Loading engineers...</p>}
            {assignError && <p role="alert" className="mb-3 text-sm text-ele-danger">{assignError}</p>}
            <div className={`gap-2 ${MODAL_ACTIONS}`}>
              <button
                type="button"
                onClick={() => setAssignTarget(null)}
                className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body ${MODAL_ACTION_BTN}`}
              >
                Cancel
              </button>
              <Button onClick={() => void doAssign()} disabled={assigning || !selectedEngineer} className={MODAL_ACTION_BTN}>
                {assigning ? "Assigning..." : "Assign"}
              </Button>
            </div>
        </ModalShell>
      )}

      {chargeTarget && (
        <CalloutChargeModal
          callout={chargeTarget}
          onClose={() => setChargeTarget(null)}
          onCharged={() => void reloadCallouts()}
        />
      )}

      {photoTarget && <CalloutPhotosModal callout={photoTarget} onClose={() => setPhotoTarget(null)} />}

      {refTarget && (
        <CalloutReportRefModal
          callout={refTarget}
          onClose={() => setRefTarget(null)}
          onSaved={() => void reloadCallouts()}
        />
      )}

      {/* Session AQY (D-AQX-4): cancel-with-attended-time. If this call-out has an open dispatch
          job, api.callouts.cancel also cancels IT atomically and returns travelLogged/
          travelSkippedReason; if not, it is the original CallOut-only cancel. */}
      {cancellingCallout && (
        <CancelJobModal
          liftLabel={`${cancellingCallout.assetRef ?? cancellingCallout.reference ?? cancellingCallout.ref} | ${cancellingCallout.siteName}`}
          onClose={() => setCancellingCallout(null)}
          onConfirm={async (body) => {
            const updated = await api.callouts.cancel(cancellingCallout.id, body);
            setCallouts((cs) => cs.map((c) => (c.id === cancellingCallout.id ? updated : c)));
            return { travelLogged: updated.travelLogged ?? false, travelSkippedReason: updated.travelSkippedReason ?? null, travelNotRechecked: updated.travelNotRechecked ?? [] };
          }}
        />
      )}

      {reportOpen && (
        <CalloutReportModal
          initial={reportInitial}
          onClose={() => setReportOpen(false)}
          onGenerated={() => void reloadCallouts()}
          onReleased={() => void reloadCallouts()}
        />
      )}

      <ConfirmDeleteModal
        open={deleteTarget !== null}
        title={`Delete call-out ${deleteTarget?.ref ?? ""}?`}
        message={DELETE_CONFIRM_MESSAGE}
        busy={deleting}
        error={deleteError}
        onConfirm={confirmDelete}
        onCancel={() => {
          if (deleting) return;
          setDeleteTarget(null);
          setDeleteError(null);
        }}
      />
    </div>
  );
}
